import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Link } from 'react-router-dom';
import './App.css';

// Header Component - Glassmorphic Floating Navbar
const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const lastScrollY = useRef(0);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // Get hero section height
      const heroSection = document.querySelector('.hero');
      if (heroSection) {
        const heroBottom = heroSection.offsetTop + heroSection.offsetHeight;
        setScrolled(currentScrollY > heroBottom - 100);
      } else {
        // No hero section (e.g., privacy page), always show scrolled state
        setScrolled(true);
      }

      // Hide on scroll down, show on scroll up (only when mobile menu is closed)
      if (!mobileMenuOpen) {
        if (currentScrollY > lastScrollY.current && currentScrollY > 100) {
          setHidden(true);
        } else {
          setHidden(false);
        }
      }

      lastScrollY.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [mobileMenuOpen]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const handleNavClick = (e, sectionId) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    const isHomePage = window.location.pathname === '/';

    if (isHomePage) {
      // On homepage, just scroll to section
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else {
      // On other pages, navigate to homepage with hash
      window.location.href = '/#' + sectionId;
    }
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  return (
    <>
      <header className={`header ${hidden && !mobileMenuOpen ? 'hidden' : ''}`}>
        <nav className={`navbar ${scrolled ? 'scrolled' : ''}`}>
          <Link to="/" className="logo">
            <div className="logo-icon">
              <svg width="28" height="28" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                <path fill="#0118A1" d="M10.306 12.482c-.15-.11-.27-.1-.791.15c-.52.25-1.241.67-2.693 1.431a.35.35 0 0 0-.16.48a.34.34 0 0 0 .47.16c1.002-.46 1.772-.81 2.253-1c-.18.59-.48 1.441-.821 2.272q-.405 1.031-.94 2.002a1.8 1.8 0 0 1-.411.56h-.13l-.38-.06c-.361-.09-.721-.22-.922-.28l-4.153-1.301l1.33-.571a.32.32 0 0 0 .191-.41a.31.31 0 0 0-.4-.18c-.64.22-2.522.92-2.543.93a.32.32 0 0 0-.19.4q.28.862.44 1.752c.18.911.311 1.822.461 2.733q.054.661.18 1.31c.06.3.223.57.46.762c.237.147.513.217.791.2q.652-.08 1.272-.3c.68-.2 1.36-.42 2.001-.651c.64-.23 1.332-.48 2.002-.73a75 75 0 0 0 2.502-1.002c.5-.22 1.001-.46 1.632-.76a4.3 4.3 0 0 0 1.321-.921c.219-.248.334-.57.32-.901a12 12 0 0 0-1.23-3.193a11.7 11.7 0 0 0-1.863-2.882m1.721 6.465a4 4 0 0 1-.76.41L7.152 21.01l-3.913 1.45c-.2.07-.48.2-.75.27s-.43.281-.53-.45c-.081-.36-.121-.74-.151-.91c-.22-.901-.4-1.812-.64-2.713q-.136-.57-.33-1.12c.57.23 1.57.64 2.571 1c.771.29 1.522.57 2.062.75q.707.272 1.451.401c.343.07.698.013 1.001-.16a6.2 6.2 0 0 0 1.462-2.522q.612-1.592 1-3.253c.3.51.661 1.25 1.002 2.002a11 11 0 0 1 1 2.872c-.04.13-.21.22-.36.32"/>
                <path fill="#0118A1" d="M15.49 1.892c.22.36.295.789.21 1.201c0 .12-.44.69-.54.951a.62.62 0 0 0 0 .53c.083.152.203.28.35.37c.18.121.42.181.57.281c.298.2.55.458.741.76a.82.82 0 0 1 .13.802q-.096.182-.25.32a2 2 0 0 1-.37.24a2.4 2.4 0 0 0-.43.19a.45.45 0 0 0-.22.3a.75.75 0 0 0 .19.561c.21.26.64.59.72.8a.88.88 0 0 1-.11.861c-.403.43-.89.77-1.431 1.001a.31.31 0 0 0-.14.42a.32.32 0 0 0 .43.14a5 5 0 0 0 1.691-1.1a1.52 1.52 0 0 0 .25-1.522a3.3 3.3 0 0 0-.49-1q.237-.114.44-.28c.19-.147.346-.331.461-.541a1.5 1.5 0 0 0 0-1.371a3.4 3.4 0 0 0-1.13-1.392l-.291-.16c.162-.268.287-.558.37-.86a2.93 2.93 0 0 0-.4-2.403a1.84 1.84 0 0 0-1.632-.8a2.19 2.19 0 0 0-1.861 1.16c-.333.85-.558 1.738-.67 2.643c-.19.866-.483 1.706-.872 2.502a6.9 6.9 0 0 1-1.511 2.062c-.18.16-.59.56-1.001.92a5 5 0 0 1-.61.451a8.4 8.4 0 0 0-1.442-1.2c.13-.944.13-1.9 0-2.843a4.8 4.8 0 0 0-.81-2.002c-.31-.42-1.001-.95-1.402-1.541a.87.87 0 0 1-.24-.761a.31.31 0 0 1 .24-.18c.21-.068.43-.098.65-.09c.48-.003.956.1 1.392.3c.13.08.15.23.2.34q.082.213.21.4q.076.106.18.18a.8.8 0 0 0 .411.16q.399.005.79-.06c.213.003.423.044.621.12c.192.082.366.201.51.351q.122.148.18.33q.067.19.06.39c0 .07-.11.341-.12.511a.53.53 0 0 0 .1.41a.35.35 0 0 0 .501 0s.18-.08.28-.9a1.9 1.9 0 0 0-.06-.67a1.7 1.7 0 0 0-.37-.541a2.15 2.15 0 0 0-.75-.64a3.75 3.75 0 0 0-1.592-.321a2.4 2.4 0 0 0-.19-.48a1.15 1.15 0 0 0-.4-.45A4.4 4.4 0 0 0 4.66.18a1.72 1.72 0 0 0-1.7 1.09a1.77 1.77 0 0 0 .21 1.482a15.6 15.6 0 0 0 1.792 1.832c.37.44.64.955.79 1.51c.177.775.248 1.57.21 2.363a2 2 0 0 0-.31-.07a3.6 3.6 0 0 0-1.691.21A2.69 2.69 0 0 0 2.118 11a3.1 3.1 0 0 0 1.251 2.443a2 2 0 0 0 0 .22q.02.324.1.64l.14.57c.063.331.24.63.501.842c.22.11.53.15.86-.28l.21-.321l.782-1.111a.36.36 0 0 0-.56-.44l-.842 1.19l-.08.12a.8.8 0 0 1-.08-.16l-.12-.55c0-.14 0-.29-.06-.44a1.1 1.1 0 0 1 0-.36a.42.42 0 0 0-.15-.36a2.33 2.33 0 0 1-1.001-1.883A1.71 1.71 0 0 1 4.27 9.59a2.55 2.55 0 0 1 1.231-.13c.388.085.739.292 1.001.59c.14.15.49.621.76.861a1 1 0 0 0 .551.29a2 2 0 0 0 1.151-.46a19 19 0 0 0 1.562-1.291a8 8 0 0 0 1.751-2.452c.446-.877.782-1.804 1.001-2.763c.08-.803.258-1.593.53-2.352a1.09 1.09 0 0 1 .931-.59c.32 0 .581.28.751.6m8.488 13.301a12.5 12.5 0 0 0-.791-3.003a2.4 2.4 0 0 0-.51-.84a1.4 1.4 0 0 0-.781-.32c-.484.002-.965.07-1.432.2q-1.04.293-2.111.44a2.56 2.56 0 0 1-1.272-.22a.36.36 0 0 0-.3.65a3.4 3.4 0 0 0 1.592.37q1.137-.081 2.252-.32q.431-.092.87-.13c.581 0 .54.21.611.39q.326.935.53 1.902c.49 2.002 0 2.162-.88 2.092c-1.151-.09-2.372-.74-3.443-.61a1.06 1.06 0 0 0-.56.24q-.273.314-.501.66q-.36.397-.771.741q-.399.346-.86.6l-1.112.491a.31.31 0 0 0-.19.4a.31.31 0 0 0 .39.2l1.221-.45a6 6 0 0 0 1.001-.61a7.5 7.5 0 0 0 .911-.761q.206-.218.38-.46a.24.24 0 0 1 .13-.09c.511.02 1.014.135 1.482.34q.86.338 1.771.49a2.87 2.87 0 0 0 1.792-.37a1.18 1.18 0 0 0 .51-.76a3.9 3.9 0 0 0 .07-1.262"/>
              </svg>
            </div>
            <span className="logo-text">Blue Pigeon</span>
          </Link>

          <div className="nav-links">
            <a href="#how-it-works" className="nav-link" onClick={(e) => handleNavClick(e, 'how-it-works')}>How It Works</a>
            <a href="#features" className="nav-link" onClick={(e) => handleNavClick(e, 'features')}>Features</a>
            <a href="#pricing" className="nav-link" onClick={(e) => handleNavClick(e, 'pricing')}>Pricing</a>
          </div>

          <div className="nav-actions">
            <a href="https://platform.bluepigeon.app" target="_blank" rel="noopener noreferrer" className="sign-in">Sign In</a>
            <button className="btn-cta-nav" onClick={() => window.open('https://platform.bluepigeon.app', '_blank')}>Free Trial</button>
          </div>

          {/* Hamburger Menu Button */}
          <button
            className={`hamburger-btn ${mobileMenuOpen ? 'open' : ''}`}
            onClick={toggleMobileMenu}
            aria-label="Toggle menu"
          >
            <span className="hamburger-line"></span>
            <span className="hamburger-line"></span>
            <span className="hamburger-line"></span>
          </button>
        </nav>
      </header>

      {/* Mobile Menu Overlay */}
      <div className={`mobile-menu-overlay ${mobileMenuOpen ? 'open' : ''}`}>
        <div className="mobile-menu-content">
          <div className="mobile-menu-links">
            <a href="#how-it-works" className="mobile-nav-link" onClick={(e) => handleNavClick(e, 'how-it-works')}>How It Works</a>
            <a href="#features" className="mobile-nav-link" onClick={(e) => handleNavClick(e, 'features')}>Features</a>
            <a href="#pricing" className="mobile-nav-link" onClick={(e) => handleNavClick(e, 'pricing')}>Pricing</a>
          </div>
          <div className="mobile-menu-actions">
            <button className="mobile-sign-in" onClick={() => { setMobileMenuOpen(false); window.open('https://platform.bluepigeon.app', '_blank'); }}>Sign In</button>
            <button className="btn-cta-mobile" onClick={() => { setMobileMenuOpen(false); window.open('https://platform.bluepigeon.app', '_blank'); }}>Free Trial</button>
          </div>
        </div>
      </div>
    </>
  );
};

// Hero Section
const Hero = () => {
  return (
    <section className="hero">
      {/* Grainy overlay */}
      <div className="grain-overlay"></div>

      {/* Grid pattern */}
      <div className="grid-pattern"></div>

      {/* Left Floating Card */}
      <div className="floating-card card-left">
        <div className="label-lead">
          <span>Opportunity</span>
          <svg width="16" height="20" viewBox="0 0 16 20" fill="none">
            <path d="M8 0 C 6 6, 2 10, 4 18" stroke="#374151" strokeWidth="1.2" fill="none"/>
            <path d="M1 14 L 4 18 L 6 14" stroke="#374151" strokeWidth="1.2" fill="none" strokeLinecap="round"/>
          </svg>
        </div>
        <div className="card-outer-frame">
          <div className="card-inner reddit-card-ui">
            {/* Reply Indicator - inside card */}
            <div className="reply-indicator">
              <span>Reply</span>
              <svg width="14" height="24" viewBox="0 0 14 24" fill="none">
                <path d="M7 0 C 9 8, 12 12, 10 22" stroke="#374151" strokeWidth="1.2" fill="none"/>
                <path d="M7 18 L 10 22 L 12 18" stroke="#374151" strokeWidth="1.2" fill="none" strokeLinecap="round"/>
              </svg>
            </div>
            {/* Reddit Post - The Lead */}
            <div className="reddit-post">
              <div className="post-header">
                <div className="subreddit-avatar">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <defs>
                      <linearGradient id="redOrangeGrad" x1="0" y1="0" x2="20" y2="20">
                        <stop stopColor="#DC2626" />
                        <stop offset="1" stopColor="#EA580C" />
                      </linearGradient>
                    </defs>
                    <circle cx="10" cy="10" r="10" fill="url(#redOrangeGrad)"/>
                    <circle cx="6" cy="9" r="1.5" fill="white"/>
                    <circle cx="14" cy="9" r="1.5" fill="white"/>
                    <ellipse cx="10" cy="13" rx="4" ry="2" fill="white"/>
                    <circle cx="3" cy="5" r="2" fill="url(#redOrangeGrad)" stroke="white" strokeWidth="1"/>
                    <circle cx="17" cy="5" r="2" fill="url(#redOrangeGrad)" stroke="white" strokeWidth="1"/>
                  </svg>
                </div>
                <div className="post-meta">
                  <span className="subreddit-name">r/SaaS</span>
                  <span className="post-time">• 3h ago</span>
                </div>
              </div>
              <p className="post-content">Any good habit tracking apps? Looking for something simple that actually works.</p>
              <div className="post-actions">
                <div className="action-votes">
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="#878A8C" strokeWidth="1.5">
                    <path d="M6 1L11 7H1L6 1Z"/>
                  </svg>
                  <span>24</span>
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="#878A8C" strokeWidth="1.5">
                    <path d="M6 11L1 5H11L6 11Z"/>
                  </svg>
                </div>
                <div className="action-comments">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#878A8C" strokeWidth="1.5">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                  </svg>
                  <span>18</span>
                </div>
              </div>
            </div>
            {/* Reply Section */}
            <div className="reddit-reply-section">
              <div className="reply-connector"></div>
              <div className="reply-content">
                <div className="reply-header">
                  <div className="reply-avatar">
                    <svg width="18" height="18" viewBox="0 0 40 40" fill="none">
                      {/* Background */}
                      <circle cx="20" cy="20" r="20" fill="#2563EB"/>
                      {/* Snoo Body */}
                      <ellipse cx="20" cy="26" rx="10" ry="8" fill="white"/>
                      {/* Snoo Head */}
                      <circle cx="20" cy="16" r="8" fill="white"/>
                      {/* Eyes */}
                      <circle cx="17" cy="15" r="1.5" fill="#2563EB"/>
                      <circle cx="23" cy="15" r="1.5" fill="#2563EB"/>
                      {/* Antenna */}
                      <line x1="20" y1="8" x2="20" y2="5" stroke="white" strokeWidth="1.5"/>
                      <circle cx="20" cy="4" r="2" fill="#2563EB"/>
                      {/* Ears */}
                      <circle cx="12" cy="12" r="2.5" fill="white"/>
                      <circle cx="28" cy="12" r="2.5" fill="white"/>
                      {/* Galaxy decoration - stars */}
                      <circle cx="8" cy="8" r="1" fill="#FCD34D"/>
                      <circle cx="32" cy="28" r="0.8" fill="#FCD34D"/>
                      <circle cx="6" cy="30" r="0.6" fill="#FCD34D"/>
                    </svg>
                  </div>
                  <span className="reply-user">galaxythreshold</span>
                  <span className="reply-time">• just now</span>
                </div>
                <p className="reply-text">Tried a bunch - the key is consistency. <strong>Streaks</strong> worked for me because it syncs with Health app and has simple UI. Start with just 2-3 habits max.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Floating Card */}
      <div className="floating-card card-right">
        <div className="label-lead">
          <span>Opportunity</span>
          <svg width="16" height="20" viewBox="0 0 16 20" fill="none">
            <path d="M8 0 C 6 6, 2 10, 4 18" stroke="#374151" strokeWidth="1.2" fill="none"/>
            <path d="M1 14 L 4 18 L 6 14" stroke="#374151" strokeWidth="1.2" fill="none" strokeLinecap="round"/>
          </svg>
        </div>
        <div className="card-outer-frame">
          <div className="card-inner reddit-card-ui">
            {/* Reply Indicator - inside card */}
            <div className="reply-indicator">
              <span>Reply</span>
              <svg width="14" height="24" viewBox="0 0 14 24" fill="none">
                <path d="M7 0 C 9 8, 12 12, 10 22" stroke="#374151" strokeWidth="1.2" fill="none"/>
                <path d="M7 18 L 10 22 L 12 18" stroke="#374151" strokeWidth="1.2" fill="none" strokeLinecap="round"/>
              </svg>
            </div>
            {/* Reddit Post - The Lead */}
            <div className="reddit-post">
              <div className="post-header">
                <div className="subreddit-avatar">
                  <svg width="20" height="20" viewBox="0 0 40 40" fill="none">
                    {/* Background */}
                    <circle cx="20" cy="20" r="20" fill="#3B82F6"/>
                    {/* Robot/Automation Icon */}
                    <rect x="12" y="12" width="16" height="14" rx="2" fill="white"/>
                    {/* Robot Eyes */}
                    <circle cx="16" cy="17" r="2" fill="#3B82F6"/>
                    <circle cx="24" cy="17" r="2" fill="#3B82F6"/>
                    {/* Robot Mouth/Grid */}
                    <rect x="14" y="22" width="12" height="2" rx="1" fill="#3B82F6"/>
                    {/* Antenna */}
                    <line x1="20" y1="12" x2="20" y2="8" stroke="white" strokeWidth="2"/>
                    <circle cx="20" cy="7" r="2" fill="#10B981"/>
                    {/* Gear accents */}
                    <circle cx="8" cy="20" r="3" fill="white" fillOpacity="0.3"/>
                    <circle cx="32" cy="20" r="3" fill="white" fillOpacity="0.3"/>
                    {/* Lightning bolt */}
                    <path d="M29 28 L32 32 L30 32 L31 36 L28 32 L30 32 Z" fill="#FCD34D"/>
                  </svg>
                </div>
                <div className="post-meta">
                  <span className="subreddit-name">r/Automation</span>
                  <span className="post-time">• 5h ago</span>
                </div>
              </div>
              <p className="post-content">Best tools for Reddit marketing? Need to grow my SaaS organically.</p>
              <div className="post-actions">
                <div className="action-votes">
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="#878A8C" strokeWidth="1.5">
                    <path d="M6 1L11 7H1L6 1Z"/>
                  </svg>
                  <span>47</span>
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="#878A8C" strokeWidth="1.5">
                    <path d="M6 11L1 5H11L6 11Z"/>
                  </svg>
                </div>
                <div className="action-comments">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#878A8C" strokeWidth="1.5">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                  </svg>
                  <span>32</span>
                </div>
              </div>
            </div>
            {/* Reply Section */}
            <div className="reddit-reply-section">
              <div className="reply-connector"></div>
              <div className="reply-content">
                <div className="reply-header">
                  <div className="reply-avatar">
                    <svg width="18" height="18" viewBox="0 0 40 40" fill="none">
                      {/* Background - chill green */}
                      <circle cx="20" cy="20" r="20" fill="#10B981"/>
                      {/* Snoo Body */}
                      <ellipse cx="20" cy="27" rx="9" ry="7" fill="white"/>
                      {/* Snoo Head */}
                      <circle cx="20" cy="16" r="8" fill="white"/>
                      {/* Relaxed/Chill Eyes - half closed */}
                      <ellipse cx="16" cy="15" rx="2" ry="1" fill="#10B981"/>
                      <ellipse cx="24" cy="15" rx="2" ry="1" fill="#10B981"/>
                      {/* Chill smile */}
                      <path d="M16 19 Q20 22 24 19" stroke="#10B981" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
                      {/* Antenna */}
                      <line x1="20" y1="8" x2="20" y2="5" stroke="white" strokeWidth="1.5"/>
                      <circle cx="20" cy="4" r="2" fill="#2563EB"/>
                      {/* Ears */}
                      <circle cx="12" cy="12" r="2.5" fill="white"/>
                      <circle cx="28" cy="12" r="2.5" fill="white"/>
                      {/* Leaf/nature decoration */}
                      <path d="M32 8 Q36 12 32 16 Q28 12 32 8Z" fill="#34D399"/>
                    </svg>
                  </div>
                  <span className="reply-user">twigchillax</span>
                  <span className="reply-time">• just now</span>
                </div>
                <p className="reply-text">Honestly, genuine engagement beats any tool. I spend 20 mins daily finding relevant posts and adding value. <strong>BluePigeon</strong> helps me find those posts faster.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Hero Content */}
      <div className="hero-content">
        <h1 className="hero-title">
          Convert Reddit Traffic into<br />
          your <span className="text-cursive">Customers</span>
        </h1>

        <p className="hero-subtitle">
          BluePigeon finds high impact reddit conversations<br />
          to help you naturally promote your brand
        </p>

        <div className="hero-buttons">
          <div className="google-btn-wrapper">
            <button className="btn-google" onClick={() => window.open('https://platform.bluepigeon.app', '_blank')}>
              <svg width="18" height="18" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Join with Google
            </button>
          </div>
          <button className="btn-cta-primary" onClick={() => window.open('https://platform.bluepigeon.app', '_blank')}>
            Get Started for Free
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M3 8H13M13 8L9 4M13 8L9 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

      </div>
    </section>
  );
};

// How It Works Section
const HowItWorks = () => {
  return (
    <section id="how-it-works" className="how-it-works">
      <div className="how-it-works-grid"></div>
      <div className="how-it-works-grain"></div>
      <div className="container">
        <div className="video-showcase">
          <div className="video-showcase-play">
            <svg width="72" height="72" viewBox="0 0 72 72" fill="none">
              <circle cx="36" cy="36" r="36" fill="rgba(255,255,255,0.25)"/>
              <circle cx="36" cy="36" r="28" fill="rgba(255,255,255,0.35)"/>
              <path d="M29 22L50 36L29 50V22Z" fill="white"/>
            </svg>
          </div>
          <div className="video-showcase-text">See BluePigeon in <em>action</em></div>
        </div>

        <div className="stats-bar">
          <div className="stat-item">
            <span className="stat-number">20K+</span>
            <span className="stat-label">Leads discovered</span>
            <span className="stat-sub">for our customers</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">5M+</span>
            <span className="stat-label">Posts analyzed</span>
            <span className="stat-sub">every month</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">300%</span>
            <span className="stat-label">Average ROI</span>
            <span className="stat-sub">within 90 days</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">5min</span>
            <span className="stat-label">Alert speed</span>
            <span className="stat-sub">real-time monitoring</span>
          </div>
        </div>

      </div>
    </section>
  );
};

// Real Opportunities Section
const RealOpportunities = () => {
  const [activeTab, setActiveTab] = useState('saas');

  const tabIcons = {
    saas: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8"/><path d="M12 17v4"/></svg>,
    consumer: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="2" width="14" height="20" rx="2"/><path d="M12 18h.01"/></svg>,
    ecommerce: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>,
    consulting: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>,
    local: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>,
  };

  const tabs = [
    { id: 'saas', label: 'SaaS' },
    { id: 'consumer', label: 'Consumer Apps' },
    { id: 'ecommerce', label: 'E-Commerce' },
    { id: 'consulting', label: 'Consulting' },
    { id: 'local', label: 'Local Biz' },
  ];

  const posts = {
    saas: [
      { sub: 'r/startups', time: '2h ago', text: 'Looking for a ', highlight: 'CRM tool', suffix: '?', upvotes: 127, comments: 43, relevance: 94 },
      { sub: 'r/SaaS', time: '4h ago', text: 'Best ', highlight: 'project management software', suffix: '?', upvotes: 89, comments: 31, relevance: 91 },
      { sub: 'r/smallbusiness', time: '6h ago', text: 'Need help finding an ', highlight: 'invoicing solution', suffix: '?', upvotes: 56, comments: 22, relevance: 88 },
    ],
    consumer: [
      { sub: 'r/apps', time: '1h ago', text: 'Any good ', highlight: 'habit tracking app', suffix: '?', upvotes: 203, comments: 67, relevance: 96 },
      { sub: 'r/productivity', time: '3h ago', text: 'Looking for a ', highlight: 'note-taking app', suffix: ' that syncs everywhere', upvotes: 145, comments: 52, relevance: 92 },
      { sub: 'r/iPhone', time: '5h ago', text: 'Best ', highlight: 'budgeting app', suffix: ' for beginners?', upvotes: 78, comments: 34, relevance: 87 },
    ],
    ecommerce: [
      { sub: 'r/ecommerce', time: '2h ago', text: 'Need a better ', highlight: 'shipping solution', suffix: ' for my store', upvotes: 112, comments: 38, relevance: 93 },
      { sub: 'r/shopify', time: '4h ago', text: 'Best ', highlight: 'email marketing tool', suffix: ' for Shopify?', upvotes: 94, comments: 41, relevance: 90 },
      { sub: 'r/dropshipping', time: '7h ago', text: 'Looking for ', highlight: 'product research tools', suffix: '', upvotes: 67, comments: 29, relevance: 86 },
    ],
    consulting: [
      { sub: 'r/consulting', time: '1h ago', text: 'Need a ', highlight: 'proposal software', suffix: ' recommendation', upvotes: 98, comments: 35, relevance: 95 },
      { sub: 'r/freelance', time: '3h ago', text: 'Best ', highlight: 'time tracking tool', suffix: ' for consultants?', upvotes: 76, comments: 28, relevance: 89 },
      { sub: 'r/business', time: '6h ago', text: 'Looking for ', highlight: 'client portal software', suffix: '', upvotes: 54, comments: 19, relevance: 85 },
    ],
    local: [
      { sub: 'r/smallbusiness', time: '2h ago', text: 'Need a ', highlight: 'booking system', suffix: ' for my salon', upvotes: 134, comments: 45, relevance: 97 },
      { sub: 'r/restaurant', time: '5h ago', text: 'Best ', highlight: 'POS system', suffix: ' for small restaurants?', upvotes: 88, comments: 33, relevance: 91 },
      { sub: 'r/localBiz', time: '8h ago', text: 'Looking for ', highlight: 'review management tools', suffix: '', upvotes: 45, comments: 17, relevance: 84 },
    ],
  };

  const activePosts = posts[activeTab];

  return (
    <section className="real-opportunities">
      <div className="container">
        <h2 className="section-title">
          Your ideal customers are<br />
          asking for help <span className="text-cursive">right now</span>
        </h2>

        <div className="opp-tabs-box">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`opp-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className="opp-tab-icon">{tabIcons[tab.id]}</span>
              {tab.label}
            </button>
          ))}
        </div>

        <div className="opp-cards" key={activeTab}>
          {activePosts.map((post, i) => (
            <div className="opp-card" key={i} style={{animationDelay: `${i * 0.08}s`}}>
              <div className="opp-card-header">
                <div className="opp-card-sub">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <circle cx="10" cy="10" r="10" fill="#FF4500"/>
                    <ellipse cx="10" cy="11" rx="7" ry="5.5" fill="white"/>
                    <circle cx="7" cy="10" r="1.5" fill="#FF4500"/>
                    <circle cx="13" cy="10" r="1.5" fill="#FF4500"/>
                    <path d="M7.5 13.5C8.5 14.5 11.5 14.5 12.5 13.5" stroke="#FF4500" strokeWidth="1" fill="none" strokeLinecap="round"/>
                    <circle cx="15.5" cy="4" r="1.5" fill="#FF4500"/>
                    <line x1="10" y1="5" x2="14.5" y2="4" stroke="white" strokeWidth="1"/>
                    <circle cx="10" cy="5" r="0.8" fill="white"/>
                  </svg>
                  <span>{post.sub}</span>
                </div>
                <div className="opp-card-time">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M12 6v6l4 2"/>
                  </svg>
                  {post.time}
                </div>
              </div>
              <p className="opp-card-text">
                {post.text}<span className="opp-highlight">{post.highlight}</span>{post.suffix}
              </p>
              <div className="opp-card-footer">
                <div className="opp-card-stats">
                  <span className="opp-stat">
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M6 1L11 7H1L6 1Z"/></svg>
                    {post.upvotes}
                  </span>
                  <span className="opp-stat">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                    {post.comments}
                  </span>
                </div>
                <div className="opp-relevance">
                  <div className="opp-relevance-bar">
                    <div className="opp-relevance-fill" style={{width: `${post.relevance}%`}}></div>
                  </div>
                  <span className="opp-relevance-pct">{post.relevance}%</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="opp-cta">
          <p className="opp-cta-text">Stop missing out on these conversations</p>
          <button className="btn-cta-primary" onClick={() => window.open('https://platform.bluepigeon.app', '_blank')}>
            Find new Customers
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M7 17L17 7"/>
              <path d="M7 7h10v10"/>
            </svg>
          </button>
        </div>
      </div>
    </section>
  );
};

// Reddit Insights Section
const RedditInsights = () => {
  return (
    <section className="reddit-insights">
      <div className="reddit-insights-grid"></div>
      <div className="reddit-insights-grain"></div>
      <div className="container">
        {/* Card 1 - Discover the Reddit Goldmine */}
        <div className="insight-card">
          <div className="insight-card-content">
            <h3 className="insight-card-title">
              Discover the Reddit <span className="text-cursive">Goldmine</span>
            </h3>
            <p className="insight-card-desc">
              Google search results are <strong>increasingly led by Reddit threads</strong>, which rank highly due to their authentic, user-driven insights. These discussions offer real opinions and recommendations that search engines favor over traditional marketing content.
            </p>
            <a href="https://x.com/ViperChill/status/1806303680188109276" target="_blank" rel="noopener noreferrer" className="insight-source-link">SOURCE</a>
          </div>
          <div className="insight-card-visual">
            <img src="/reddit.jpg" alt="The Most Prominent Sites Across 10,000 Best Product Search Results" className="insight-card-img" />
          </div>
        </div>

        {/* Card 2 - Turn Reddit Into AI Visibility */}
        <div className="insight-card insight-card-reverse">
          <div className="insight-card-visual insight-visual-ai">
            <div className="ai-flow">
              {/* Reddit Icon */}
              <div className="ai-flow-icon reddit-icon-circle">
                <svg width="36" height="36" viewBox="0 0 24 24" fill="#FF4500">
                  <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/>
                </svg>
              </div>

              {/* Line */}
              <div className="ai-flow-line-v"></div>

              {/* BluePigeon Logo */}
              <div className="ai-flow-icon bp-icon-circle">
                <svg width="32" height="32" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                  <path fill="#0118A1" d="M10.306 12.482c-.15-.11-.27-.1-.791.15c-.52.25-1.241.67-2.693 1.431a.35.35 0 0 0-.16.48a.34.34 0 0 0 .47.16c1.002-.46 1.772-.81 2.253-1c-.18.59-.48 1.441-.821 2.272q-.405 1.031-.94 2.002a1.8 1.8 0 0 1-.411.56h-.13l-.38-.06c-.361-.09-.721-.22-.922-.28l-4.153-1.301l1.33-.571a.32.32 0 0 0 .191-.41a.31.31 0 0 0-.4-.18c-.64.22-2.522.92-2.543.93a.32.32 0 0 0-.19.4q.28.862.44 1.752c.18.911.311 1.822.461 2.733q.054.661.18 1.31c.06.3.223.57.46.762c.237.147.513.217.791.2q.652-.08 1.272-.3c.68-.2 1.36-.42 2.001-.651c.64-.23 1.332-.48 2.002-.73a75 75 0 0 0 2.502-1.002c.5-.22 1.001-.46 1.632-.76a4.3 4.3 0 0 0 1.321-.921c.219-.248.334-.57.32-.901a12 12 0 0 0-1.23-3.193a11.7 11.7 0 0 0-1.863-2.882m1.721 6.465a4 4 0 0 1-.76.41L7.152 21.01l-3.913 1.45c-.2.07-.48.2-.75.27s-.43.281-.53-.45c-.081-.36-.121-.74-.151-.91c-.22-.901-.4-1.812-.64-2.713q-.136-.57-.33-1.12c.57.23 1.57.64 2.571 1c.771.29 1.522.57 2.062.75q.707.272 1.451.401c.343.07.698.013 1.001-.16a6.2 6.2 0 0 0 1.462-2.522q.612-1.592 1-3.253c.3.51.661 1.25 1.002 2.002a11 11 0 0 1 1 2.872c-.04.13-.21.22-.36.32"/>
                  <path fill="#0118A1" d="M15.49 1.892c.22.36.295.789.21 1.201c0 .12-.44.69-.54.951a.62.62 0 0 0 0 .53c.083.152.203.28.35.37c.18.121.42.181.57.281c.298.2.55.458.741.76a.82.82 0 0 1 .13.802q-.096.182-.25.32a2 2 0 0 1-.37.24a2.4 2.4 0 0 0-.43.19a.45.45 0 0 0-.22.3a.75.75 0 0 0 .19.561c.21.26.64.59.72.8a.88.88 0 0 1-.11.861c-.403.43-.89.77-1.431 1.001a.31.31 0 0 0-.14.42a.32.32 0 0 0 .43.14a5 5 0 0 0 1.691-1.1a1.52 1.52 0 0 0 .25-1.522a3.3 3.3 0 0 0-.49-1q.237-.114.44-.28c.19-.147.346-.331.461-.541a1.5 1.5 0 0 0 0-1.371a3.4 3.4 0 0 0-1.13-1.392l-.291-.16c.162-.268.287-.558.37-.86a2.93 2.93 0 0 0-.4-2.403a1.84 1.84 0 0 0-1.632-.8a2.19 2.19 0 0 0-1.861 1.16c-.333.85-.558 1.738-.67 2.643c-.19.866-.483 1.706-.872 2.502a6.9 6.9 0 0 1-1.511 2.062c-.18.16-.59.56-1.001.92a5 5 0 0 1-.61.451a8.4 8.4 0 0 0-1.442-1.2c.13-.944.13-1.9 0-2.843a4.8 4.8 0 0 0-.81-2.002c-.31-.42-1.001-.95-1.402-1.541a.87.87 0 0 1-.24-.761a.31.31 0 0 1 .24-.18c.21-.068.43-.098.65-.09c.48-.003.956.1 1.392.3c.13.08.15.23.2.34q.082.213.21.4q.076.106.18.18a.8.8 0 0 0 .411.16q.399.005.79-.06c.213.003.423.044.621.12c.192.082.366.201.51.351q.122.148.18.33q.067.19.06.39c0 .07-.11.341-.12.511a.53.53 0 0 0 .1.41a.35.35 0 0 0 .501 0s.18-.08.28-.9a1.9 1.9 0 0 0-.06-.67a1.7 1.7 0 0 0-.37-.541a2.15 2.15 0 0 0-.75-.64a3.75 3.75 0 0 0-1.592-.321a2.4 2.4 0 0 0-.19-.48a1.15 1.15 0 0 0-.4-.45A4.4 4.4 0 0 0 4.66.18a1.72 1.72 0 0 0-1.7 1.09a1.77 1.77 0 0 0 .21 1.482a15.6 15.6 0 0 0 1.792 1.832c.37.44.64.955.79 1.51c.177.775.248 1.57.21 2.363a2 2 0 0 0-.31-.07a3.6 3.6 0 0 0-1.691.21A2.69 2.69 0 0 0 2.118 11a3.1 3.1 0 0 0 1.251 2.443a2 2 0 0 0 0 .22q.02.324.1.64l.14.57c.063.331.24.63.501.842c.22.11.53.15.86-.28l.21-.321l.782-1.111a.36.36 0 0 0-.56-.44l-.842 1.19l-.08.12a.8.8 0 0 1-.08-.16l-.12-.55c0-.14 0-.29-.06-.44a1.1 1.1 0 0 1 0-.36a.42.42 0 0 0-.15-.36a2.33 2.33 0 0 1-1.001-1.883A1.71 1.71 0 0 1 4.27 9.59a2.55 2.55 0 0 1 1.231-.13c.388.085.739.292 1.001.59c.14.15.49.621.76.861a1 1 0 0 0 .551.29a2 2 0 0 0 1.151-.46a19 19 0 0 0 1.562-1.291a8 8 0 0 0 1.751-2.452c.446-.877.782-1.804 1.001-2.763c.08-.803.258-1.593.53-2.352a1.09 1.09 0 0 1 .931-.59c.32 0 .581.28.751.6m8.488 13.301a12.5 12.5 0 0 0-.791-3.003a2.4 2.4 0 0 0-.51-.84a1.4 1.4 0 0 0-.781-.32c-.484.002-.965.07-1.432.2q-1.04.293-2.111.44a2.56 2.56 0 0 1-1.272-.22a.36.36 0 0 0-.3.65a3.4 3.4 0 0 0 1.592.37q1.137-.081 2.252-.32q.431-.092.87-.13c.581 0 .54.21.611.39q.326.935.53 1.902c.49 2.002 0 2.162-.88 2.092c-1.151-.09-2.372-.74-3.443-.61a1.06 1.06 0 0 0-.56.24q-.273.314-.501.66q-.36.397-.771.741q-.399.346-.86.6l-1.112.491a.31.31 0 0 0-.19.4a.31.31 0 0 0 .39.2l1.221-.45a6 6 0 0 0 1.001-.61a7.5 7.5 0 0 0 .911-.761q.206-.218.38-.46a.24.24 0 0 1 .13-.09c.511.02 1.014.135 1.482.34q.86.338 1.771.49a2.87 2.87 0 0 0 1.792-.37a1.18 1.18 0 0 0 .51-.76a3.9 3.9 0 0 0 .07-1.262"/>
                </svg>
              </div>

              {/* Line */}
              <div className="ai-flow-line-v"></div>

              {/* Overlapping AI Logo Cards */}
              <div className="ai-logo-stack">
                {/* Perplexity */}
                <div className="ai-logo-card ai-logo-1">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="#20808D">
                    <path d="M22.3977 7.0896h-2.3106V.0676l-7.5094 6.3542V.1577h-1.1554v6.1966L4.4904 0v7.0896H1.6023v10.3976h2.8882V24l6.932-6.3591v6.2005h1.1554v-6.0469l6.9318 6.1807v-6.4879h2.8882V7.0896zm-3.4657-4.531v4.531h-5.355l5.355-4.531zm-13.2862.0676 4.8691 4.4634H5.6458V2.6262zM2.7576 16.332V8.245h7.8476l-6.1149 6.1147v1.9723H2.7576zm2.8882 5.0404v-3.8852h.0001v-2.6488l5.7763-5.7764v7.0111l-5.7764 5.2993zm12.7086.0248-5.7766-5.1509V9.0618l5.7766 5.7766v6.5588zm2.8882-5.0652h-1.733v-1.9723L13.3948 8.245h7.8478v8.087z"/>
                  </svg>
                </div>
                {/* ChatGPT / OpenAI */}
                <div className="ai-logo-card ai-logo-2">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="#000">
                    <path d="M22.282 9.821a5.985 5.985 0 0 0-.516-4.91 6.046 6.046 0 0 0-6.51-2.9A6.065 6.065 0 0 0 4.981 4.18a5.985 5.985 0 0 0-3.998 2.9 6.046 6.046 0 0 0 .743 7.097 5.98 5.98 0 0 0 .51 4.911 6.051 6.051 0 0 0 6.515 2.9A5.985 5.985 0 0 0 13.26 24a6.056 6.056 0 0 0 5.772-4.206 5.99 5.99 0 0 0 3.997-2.9 6.056 6.056 0 0 0-.747-7.073zM13.26 22.43a4.476 4.476 0 0 1-2.876-1.04l.141-.081 4.779-2.758a.795.795 0 0 0 .392-.681v-6.737l2.02 1.168a.071.071 0 0 1 .038.052v5.583a4.504 4.504 0 0 1-4.494 4.494zM3.6 18.304a4.47 4.47 0 0 1-.535-3.014l.142.085 4.783 2.759a.771.771 0 0 0 .78 0l5.843-3.369v2.332a.08.08 0 0 1-.033.062L9.74 19.95a4.5 4.5 0 0 1-6.14-1.646zM2.34 7.896a4.485 4.485 0 0 1 2.366-1.973V11.6a.766.766 0 0 0 .388.676l5.815 3.355-2.02 1.168a.076.076 0 0 1-.071 0l-4.83-2.786A4.504 4.504 0 0 1 2.34 7.872zm16.597 3.855l-5.833-3.387L15.119 7.2a.076.076 0 0 1 .071 0l4.83 2.791a4.494 4.494 0 0 1-.676 8.105v-5.678a.79.79 0 0 0-.407-.667zm2.01-3.023l-.141-.085-4.774-2.782a.776.776 0 0 0-.785 0L9.409 9.23V6.897a.066.066 0 0 1 .028-.061l4.83-2.787a4.5 4.5 0 0 1 6.68 4.66zm-12.64 4.135l-2.02-1.164a.08.08 0 0 1-.038-.057V6.075a4.5 4.5 0 0 1 7.375-3.453l-.142.08L8.704 5.46a.795.795 0 0 0-.393.681zm1.097-2.365l2.602-1.5 2.607 1.5v2.999l-2.597 1.5-2.612-1.5z"/>
                  </svg>
                </div>
                {/* Gemini */}
                <div className="ai-logo-card ai-logo-3">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="url(#gemGrad)">
                    <defs>
                      <linearGradient id="gemGrad" x1="0" y1="0" x2="24" y2="24">
                        <stop stopColor="#4285F4"/>
                        <stop offset="0.5" stopColor="#9B72CB"/>
                        <stop offset="1" stopColor="#D96570"/>
                      </linearGradient>
                    </defs>
                    <path d="M11.04 19.32Q12 21.51 12 24q0-2.49.93-4.68.96-2.19 2.58-3.81t3.81-2.55Q21.51 12 24 12q-2.49 0-4.68-.93a12.3 12.3 0 0 1-3.81-2.58 12.3 12.3 0 0 1-2.58-3.81Q12 2.49 12 0q0 2.49-.96 4.68-.93 2.19-2.55 3.81a12.3 12.3 0 0 1-3.81 2.58Q2.49 12 0 12q2.49 0 4.68.96 2.19.93 3.81 2.55t2.55 3.81"/>
                  </svg>
                </div>
                {/* Google */}
                <div className="ai-logo-card ai-logo-4">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                </div>
              </div>
            </div>
          </div>
          <div className="insight-card-content">
            <h3 className="insight-card-title">
              Turn Reddit Into <span className="text-cursive">AI Visibility</span>
            </h3>
            <p className="insight-card-desc">
              AI tools like ChatGPT, Perplexity and Gemini use these discussions to shape product recommendations, making strategic Reddit visibility key for both SEO and AI-driven discovery.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

// Tools Bento Section
const ToolsBento = () => {
  return (
    <section id="features" className="tools-bento">
      <div className="tools-bento-grid-bg"></div>
      <div className="tools-bento-grain"></div>
      <div className="container">
        <h2 className="section-title">
          All the tools to find <span className="text-cursive">your customers</span>
        </h2>
        <p className="section-subtitle">
          Powerful features designed to help you discover, qualify, and engage with potential customers.
        </p>

        <div className="bento-grid">
          {/* Smart Keywords */}
          <div className="bento-card bento-sm">
            <div className="bento-visual">
              <svg width="120" height="120" viewBox="0 0 120 60" fill="none">
                {/* Track */}
                <rect x="10" y="12" width="100" height="36" rx="18" fill="#d1d5db"/>
                {/* Track inner */}
                <rect x="14" y="16" width="92" height="28" rx="14" fill="#e5e7eb"/>
                {/* Knob shadow */}
                <circle cx="76" cy="30" r="16" fill="#9ca3af"/>
                {/* Knob */}
                <circle cx="76" cy="29" r="14" fill="#b0b5be"/>
                {/* Knob highlight */}
                <circle cx="72" cy="25" r="4" fill="white" opacity="0.3"/>
                {/* Line marks on track */}
                <rect x="26" y="26" width="14" height="3" rx="1.5" fill="#9ca3af"/>
                <rect x="26" y="32" width="10" height="3" rx="1.5" fill="#9ca3af"/>
              </svg>
            </div>
            <h4 className="bento-title">Smart Keywords</h4>
            <p className="bento-desc">Intelligent keyword rules that understand context and intent, not just exact matches.</p>
          </div>

          {/* AI Lead Scoring */}
          <div className="bento-card bento-sm">
            <div className="bento-visual">
              <svg width="120" height="120" viewBox="0 0 100 100" fill="none">
                {/* Left pill - "1" */}
                <rect x="14" y="14" width="32" height="72" rx="16" fill="#d1d5db"/>
                <rect x="18" y="18" width="24" height="64" rx="12" fill="#c8ccd2"/>
                <rect x="22" y="24" width="16" height="52" rx="8" fill="#9ca3af"/>
                <text x="30" y="58" textAnchor="middle" fill="white" fontSize="30" fontWeight="800" fontFamily="Urbanist, sans-serif">1</text>

                {/* Dotted line separator */}
                <line x1="50" y1="22" x2="50" y2="78" stroke="#9ca3af" strokeWidth="2" strokeDasharray="4 4"/>

                {/* Right pill - "0" */}
                <rect x="54" y="14" width="32" height="72" rx="16" fill="#d1d5db"/>
                <rect x="58" y="18" width="24" height="64" rx="12" fill="#c8ccd2"/>
                <rect x="62" y="24" width="16" height="52" rx="8" fill="#9ca3af"/>
                <text x="70" y="58" textAnchor="middle" fill="white" fontSize="30" fontWeight="800" fontFamily="Urbanist, sans-serif">0</text>
              </svg>
            </div>
            <h4 className="bento-title">AI Lead Scoring</h4>
            <p className="bento-desc">Our AI analyzes each post to score lead quality based on buying intent, sentiment, and relevance.</p>
          </div>

          {/* Collaborate & Promote - tall card */}
          <div className="bento-card bento-tall">
            <div className="collab-visual">
              {/* Row 0 - extra top */}
              <div className="collab-icon faded"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/></svg></div>
              <div className="collab-icon faded"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg></div>
              <div className="collab-icon faded"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg></div>
              <div className="collab-icon faded"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg></div>
              <div className="collab-icon faded"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round"><path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5"/><path d="M9 18h6"/></svg></div>
              {/* Row 1 */}
              <div className="collab-icon faded"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg></div>
              <div className="collab-icon faded"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><path d="M22 6l-10 7L2 6"/></svg></div>
              <div className="collab-icon faded"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg></div>
              <div className="collab-icon faded"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg></div>
              <div className="collab-icon faded"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg></div>
              {/* Row 2: col1, then center icon spans 3 cols & 3 rows, col5 */}
              <div className="collab-icon faded"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg></div>
              {/* CENTER - Big metallic collaborate icon */}
              <div className="collab-icon highlighted">
                <svg width="100%" height="100%" viewBox="0 0 100 100" fill="none">
                  <circle cx="50" cy="50" r="48" fill="#d1d5db"/>
                  <circle cx="50" cy="50" r="42" fill="#e5e7eb"/>
                  <circle cx="50" cy="50" r="34" fill="#f3f4f6"/>
                  <circle cx="38" cy="40" r="7" fill="#9ca3af"/>
                  <path d="M24 62c0-8 6-14 14-14h0c8 0 14 6 14 14" fill="#9ca3af"/>
                  <circle cx="62" cy="40" r="7" fill="#b0b5be"/>
                  <path d="M48 62c0-8 6-14 14-14h0c8 0 14 6 14 14" fill="#b0b5be"/>
                  <circle cx="35" cy="37" r="2.5" fill="white" opacity="0.3"/>
                </svg>
              </div>
              <div className="collab-icon faded"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></svg></div>
              {/* Row 3: col1 & col5 */}
              <div className="collab-icon faded"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><path d="M2 12h20"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg></div>
              <div className="collab-icon faded"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg></div>
              {/* Row 4: col1 & col5 */}
              <div className="collab-icon faded"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/><path d="M16 13H8"/><path d="M16 17H8"/></svg></div>
              <div className="collab-icon faded"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round"><path d="M23 6l-13.5 13.5-4-4L1 20"/></svg></div>
              {/* Row 5 */}
              <div className="collab-icon faded"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg></div>
              <div className="collab-icon faded"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg></div>
              <div className="collab-icon faded"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg></div>
              <div className="collab-icon faded"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round"><path d="M18 20V10M12 20V4M6 20v-6"/></svg></div>
              <div className="collab-icon faded"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round"><path d="M9 11l3 3L22 4"/></svg></div>
              {/* Row 6 - extra bottom */}
              <div className="collab-icon faded"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round"><rect x="2" y="2" width="20" height="20" rx="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><path d="M17.5 6.5h.01"/></svg></div>
              <div className="collab-icon faded"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round"><rect x="1" y="4" width="22" height="16" rx="2"/><path d="M1 10h22"/></svg></div>
              <div className="collab-icon faded"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg></div>
              <div className="collab-icon faded"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg></div>
              <div className="collab-icon faded"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg></div>
            </div>

            <h4 className="bento-title">Collaborate & Promote</h4>
            <p className="bento-desc">Collaborate with brand promotion teams and promote your brand from various perspectives across Reddit.</p>
          </div>

          {/* Reply Management */}
          <div className="bento-card bento-sm">
            <div className="bento-visual">
              <svg width="120" height="120" viewBox="0 0 100 100" fill="none">
                {/* Envelope body */}
                <rect x="10" y="32" width="80" height="52" rx="6" fill="#d1d5db"/>
                {/* Envelope flap */}
                <path d="M10 32l40 28 40-28" fill="#e5e7eb"/>
                <path d="M10 32l40 28 40-28" stroke="#c0c5cc" strokeWidth="1"/>
                {/* Letter coming out */}
                <rect x="22" y="14" width="56" height="40" rx="4" fill="white" stroke="#d1d5db" strokeWidth="1"/>
                {/* Letter lines */}
                <rect x="32" y="26" width="36" height="3" rx="1.5" fill="#d1d5db"/>
                <rect x="32" y="33" width="28" height="3" rx="1.5" fill="#e5e7eb"/>
                <rect x="32" y="40" width="20" height="3" rx="1.5" fill="#e5e7eb"/>
                {/* Checkmark on letter */}
                <circle cx="70" cy="22" r="6" fill="#22C55E" opacity="0.8"/>
                <path d="M67 22l2 2 4-4" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h4 className="bento-title">Reply Management</h4>
            <p className="bento-desc">Manage and track your replies. Mark leads as contacted to avoid duplicate outreach.</p>
          </div>

          {/* Competitor Monitoring */}
          <div className="bento-card bento-sm">
            <div className="bento-visual">
              <svg width="120" height="120" viewBox="0 0 100 100" fill="none">
                {/* Outer ring */}
                <circle cx="50" cy="50" r="40" fill="#d1d5db"/>
                <circle cx="50" cy="50" r="36" fill="#e5e7eb"/>
                <circle cx="50" cy="50" r="30" fill="#f3f4f6"/>
                {/* Middle ring */}
                <circle cx="50" cy="50" r="20" fill="#d1d5db"/>
                <circle cx="50" cy="50" r="16" fill="#e5e7eb"/>
                {/* Center dot */}
                <circle cx="50" cy="50" r="7" fill="#9ca3af"/>
                <circle cx="50" cy="50" r="4" fill="#6b7280"/>
                {/* Crosshair lines */}
                <rect x="48" y="8" width="4" height="16" rx="2" fill="#b0b5be"/>
                <rect x="48" y="76" width="4" height="16" rx="2" fill="#b0b5be"/>
                <rect x="8" y="48" width="16" height="4" rx="2" fill="#b0b5be"/>
                <rect x="76" y="48" width="16" height="4" rx="2" fill="#b0b5be"/>
                {/* Highlight */}
                <circle cx="42" cy="38" r="3" fill="white" opacity="0.2"/>
              </svg>
            </div>
            <h4 className="bento-title">Competitor Monitoring</h4>
            <p className="bento-desc">Track when people mention a competitor and swoop in with a better solution.</p>
          </div>
        </div>
      </div>
    </section>
  );
};

// Steps Section
const StepsScoreBar = ({ score }) => {
  const color = score >= 90 ? 'var(--blue-primary)' : score >= 85 ? '#60A5FA' : 'var(--green)';
  return (
    <div className="steps-score-bar">
      <div className="steps-score-track">
        <div className="steps-score-fill" style={{width: `${score}%`, background: color}}></div>
      </div>
      <span className="steps-score-pct" style={{color}}>{score}%</span>
    </div>
  );
};

const StepsSection = () => {
  const steps = [
    {
      num: '1',
      cardTitle: 'Add Your Website',
      headline: 'Set Up Your Brand',
      description: 'Enter your domain. We analyze your site, features, audience, and positioning automatically.',
      content: (
        <div className="step-card-body">
          <div className="step-url-input">
            <span className="step-url-prefix">https://</span>
            <span className="step-url-placeholder">yoursite.com</span>
          </div>
          <p className="step-url-hint">Enter your domain and let us do the research.</p>
          <div className="step-analyze-btn">Analyze</div>
        </div>
      ),
    },
    {
      num: '2',
      cardTitle: 'Discovered Targets',
      headline: 'Get Smart Targets',
      description: 'AI picks the best keywords and subreddits for your brand. Add or adjust as you like.',
      content: (
        <div className="step-targets">
          {[
            { sub: 'r/SaaS', keyword: 'AI workflow automation', score: 96 },
            { sub: 'r/entrepreneur', keyword: 'no code AI agents', score: 91 },
            { sub: 'r/smallbusiness', keyword: 'lead generation tool', score: 87 },
          ].map((item, i) => (
            <div key={i} className="step-target-row">
              <div className="step-target-header">
                <span className="step-target-sub">{item.sub}</span>
                <StepsScoreBar score={item.score} />
              </div>
              <p className="step-target-kw">"{item.keyword}"</p>
            </div>
          ))}
        </div>
      ),
    },
    {
      num: '3',
      cardTitle: 'Command Your AI',
      headline: 'Set the Tone',
      description: 'Configure how AI qualifies leads, crafts replies, and generates brand posts.',
      content: (
        <div className="step-prompts">
          {[
            { label: 'Analysis', desc: 'Find problem & competitor leads', color: '#EFF6FF', iconD: 'M11 11m-8 0a8 8 0 1 0 16 0 8 8 0 1 0-16 0M21 21l-4.35-4.35' },
            { label: 'Reply', desc: 'Sound genuine, not salesy', color: '#F0FDF4', iconD: 'M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z' },
            { label: 'Post', desc: 'Create posts in top subreddits', color: '#EFF6FF', iconD: 'M12 20h9M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z' },
          ].map((item, i) => (
            <div key={i} className="step-prompt-row">
              <div className="step-prompt-icon" style={{background: item.color}}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--blue-primary)" strokeWidth="2" strokeLinecap="round"><path d={item.iconD}/></svg>
              </div>
              <div>
                <div className="step-prompt-label">{item.label} Prompt</div>
                <div className="step-prompt-desc">{item.desc}</div>
              </div>
            </div>
          ))}
        </div>
      ),
    },
    {
      num: '4',
      cardTitle: 'Link Reddit Account',
      headline: 'Connect & Launch',
      description: 'Link your Reddit account and share your writing style. Post directly from BluePigeon.',
      content: (
        <div className="step-connect">
          <div className="step-connect-row">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="#FF4500"><path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/></svg>
            <div>
              <div className="step-connect-title">Reddit Connected</div>
              <div className="step-connect-status">u/your_account ✓</div>
            </div>
          </div>
          <div className="step-writing-sample">
            <div className="step-writing-header">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round"><line x1="17" y1="10" x2="3" y2="10"/><line x1="21" y1="6" x2="3" y2="6"/><line x1="21" y1="14" x2="3" y2="14"/><line x1="17" y1="18" x2="3" y2="18"/></svg>
              <span>Writing Style Sample</span>
            </div>
            <p className="step-writing-text">"Hey, I actually ran into this same problem last month and here is what worked for me..."</p>
          </div>
        </div>
      ),
    },
    {
      num: '5',
      cardTitle: 'Opportunities Found',
      headline: 'See Results',
      description: 'One click discovery across thousands of posts. Engage directly from the platform.',
      content: (
        <div className="step-results">
          {[
            { sub: 'r/SaaS', text: 'Need a tool for building AI agents...', status: 'Reply Ready', color: 'var(--blue-primary)' },
            { sub: 'r/entrepreneur', text: 'Looking for workflow automation...', status: 'Sent', color: 'var(--green)' },
          ].map((item, i) => (
            <div key={i} className="step-result-row">
              <div className="step-result-header">
                <span className="step-target-sub">{item.sub}</span>
                <span className="step-result-status" style={{color: item.color, background: `${item.color}15`}}>{item.status}</span>
              </div>
              <p className="step-target-kw">"{item.text}"</p>
            </div>
          ))}
          <div className="step-cta-btn">Send Reply</div>
        </div>
      ),
    },
  ];

  return (
    <section className="steps-section">
      <div className="steps-section-grid-bg"></div>
      <div className="steps-section-grain"></div>
      <div className="wave-glow"></div>
      <div className="container">
        <span className="section-label">HOW IT WORKS</span>
        <h2 className="section-title">
          Five <span className="text-cursive">simple</span> steps to find customers
        </h2>
        <p className="section-subtitle">
          From your website to your next customer in under 10 minutes.
        </p>

        <div className="steps-row">
          {steps.map((step, i) => (
            <React.Fragment key={i}>
              <div className="step-outer-card">
                <div className="step-inner-card">
                  <div className="step-number">{step.num}</div>
                  <div className="step-card-title">
                    <span className="step-card-plus">+</span>
                    {step.cardTitle}
                  </div>
                  {step.content}
                </div>
                <div className="step-info">
                  <h4 className="step-headline">{step.headline}</h4>
                  <p className="step-desc">{step.description}</p>
                </div>
              </div>
              {i < steps.length - 1 && (
                <div className="step-arrow">→</div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    </section>
  );
};

// Features Section
const Features = () => {
  return (
    <section id="features" className="features">
      {/* Grid pattern - fading out from top */}
      <div className="features-grid"></div>
      {/* Grainy texture - fading out from top */}
      <div className="features-grain"></div>
      <div className="container">
        <span className="section-label">FEATURES</span>
        <h2 className="section-title">
          Grow Your Product on <span className="text-gradient">Auto-Pilot</span>
        </h2>
        <p className="section-subtitle">
          Everything you need to own your niche on Reddit.
        </p>

        {/* Feature 1 */}
        <div className="feature-block">
          <div className="feature-content">
            <h3 className="feature-title">
              Find your <span className="text-gradient" style={{whiteSpace: 'nowrap'}}>ideal customers</span><br />automatically
            </h3>
            <p className="feature-desc">
              BluePigeon scans Reddit 24/7 and surfaces all conversations where people are <strong>actively looking</strong> for tools & brands like yours.
            </p>
            <button className="btn-cta-primary" onClick={() => window.open('https://platform.bluepigeon.app', '_blank')}>
              Get Started for Free Now
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M3 8H13M13 8L9 4M13 8L9 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
          <div className="feature-visual feature-visual-clean">
            <div className="leads-discovery-panel">
              {/* Floating Search Icon */}
              <div className="floating-search-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"/>
                  <path d="M21 21l-4.35-4.35"/>
                </svg>
              </div>

              {/* Subreddit Cards with Connecting Line */}
              <div className="subreddit-cards-container">
                <div className="connecting-line">
                  <div className="line-glow"></div>
                  <div className="line-dot dot-1"></div>
                  <div className="line-dot dot-2"></div>
                  <div className="line-dot dot-3"></div>
                </div>

                <div className="subreddit-card">
                  <div className="subreddit-card-header">
                    <div className="subreddit-icon">r/</div>
                    <div className="subreddit-info">
                      <span className="subreddit-name-text">r/SaaS</span>
                      <span className="subreddit-time">2h ago</span>
                    </div>
                    <div className="match-badge">
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <circle cx="7" cy="7" r="7" fill="#10B981"/>
                        <path d="M4 7L6 9L10 5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span>95%</span>
                    </div>
                  </div>
                  <p className="subreddit-post-text">Looking for marketing automation tools for my B2B...</p>
                </div>

                <div className="subreddit-card">
                  <div className="subreddit-card-header">
                    <div className="subreddit-icon">r/</div>
                    <div className="subreddit-info">
                      <span className="subreddit-name-text">r/b2c</span>
                      <span className="subreddit-time">4h ago</span>
                    </div>
                    <div className="match-badge">
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <circle cx="7" cy="7" r="7" fill="#10B981"/>
                        <path d="M4 7L6 9L10 5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span>90%</span>
                    </div>
                  </div>
                  <p className="subreddit-post-text">How do you find customers on Reddit without being...</p>
                </div>

                <div className="subreddit-card">
                  <div className="subreddit-card-header">
                    <div className="subreddit-icon">r/</div>
                    <div className="subreddit-info">
                      <span className="subreddit-name-text">r/marketing</span>
                      <span className="subreddit-time">6h ago</span>
                    </div>
                    <div className="match-badge">
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <circle cx="7" cy="7" r="7" fill="#10B981"/>
                        <path d="M4 7L6 9L10 5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span>100%</span>
                    </div>
                  </div>
                  <p className="subreddit-post-text">Best tools for Reddit outreach? We need to scale our...</p>
                </div>
              </div>

              {/* Floating Leads Badge */}
              <div className="floating-leads-badge">
                <span className="badge-dot"></span>
                3 High-Intent Leads Found
              </div>
            </div>
          </div>
        </div>

        {/* Feature 2 */}
        <div className="feature-block reverse">
          <div className="feature-content">
            <h3 className="feature-title">
              Reply to <span className="text-gradient">high-intent posts</span><br />automatically
            </h3>
            <p className="feature-desc">
              BluePigeon writes and sends helpful replies to posts where people <strong>ask for tools & brands</strong> or describe problems your product can solve.
            </p>
            <button className="btn-cta-primary" onClick={() => window.open('https://platform.bluepigeon.app', '_blank')}>
              Start Sending Replies
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M3 8H13M13 8L9 4M13 8L9 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
          <div className="feature-visual feature-visual-clean">
            <div className="reply-discovery-panel">
              {/* Big Floating Send Icon - Top Left */}
              <div className="floating-send-icon-big">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M22 2L11 13M22 2L15 22L11 13M22 2L2 9L11 13" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>

              {/* Subreddit Reply Cards with Connecting Line */}
              <div className="reply-cards-container">
                <div className="connecting-line-left">
                  <div className="line-glow-left"></div>
                  <div className="line-dot-left dot-l1"></div>
                  <div className="line-dot-left dot-l2"></div>
                </div>

                <div className="reply-subreddit-card">
                  {/* Small floating send icon */}
                  <div className="card-send-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <path d="M22 2L11 13M22 2L15 22L11 13M22 2L2 9L11 13" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div className="reply-card-header">
                    <div className="subreddit-icon">r/</div>
                    <span className="subreddit-name-text">r/SaaS</span>
                  </div>
                  <p className="reply-post-question">What tools do you use for Reddit marketing?</p>
                  <div className="your-reply-section">
                    <span className="your-reply-label">← YOUR REPLY</span>
                    <p className="your-reply-text">For automatically finding, and DMing leads on Reddit, I use...</p>
                  </div>
                </div>

                <div className="reply-subreddit-card">
                  {/* Small floating send icon */}
                  <div className="card-send-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <path d="M22 2L11 13M22 2L15 22L11 13M22 2L2 9L11 13" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div className="reply-card-header">
                    <div className="subreddit-icon">r/</div>
                    <span className="subreddit-name-text">r/startups</span>
                  </div>
                  <p className="reply-post-question">How to find early customers without ads?</p>
                  <div className="your-reply-section">
                    <span className="your-reply-label">← YOUR REPLY</span>
                    <p className="your-reply-text">Reddit has been amazing for us. Here's what worked...</p>
                  </div>
                </div>
              </div>

              {/* Floating Send Replies Button */}
              <div className="floating-send-btn">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M14 2L7 9M14 2L9.5 14L7 9M14 2L2 6.5L7 9" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Send 2 Replies
              </div>
            </div>
          </div>
        </div>

        {/* Feature 3 */}
        <div className="feature-block">
          <div className="feature-content">
            <h3 className="feature-title">
              <span className="text-green">Full control</span>, safer<br />automation
            </h3>
            <p className="feature-desc">
              Our extension runs through your own Reddit account and browser, and <strong>never sends anything without your approval</strong>.
            </p>
            <button className="btn-cta-green" onClick={() => window.open('https://platform.bluepigeon.app', '_blank')}>Start Automated Outreach</button>
          </div>
          <div className="feature-visual feature-visual-clean">
            <div className="control-discovery-panel">
              {/* Big Floating Shield Icon - Top Left */}
              <div className="floating-shield-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>

              {/* Control Cards with Connecting Line */}
              <div className="control-cards-container">
                <div className="connecting-line-green">
                  <div className="line-glow-green"></div>
                  <div className="line-dot-green dot-g1"></div>
                  <div className="line-dot-green dot-g2"></div>
                </div>

                {/* Post Card - Approved & Posted */}
                <div className="control-message-card">
                  <div className="card-status-icon approved">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <path d="M20 6L9 17L4 12" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div className="control-card-header">
                    <span className="message-type-badge post">POST</span>
                    <div className="control-user-info">
                      <span className="control-username">r/smallbusiness</span>
                      <span className="control-status approved">Approved & Posted</span>
                    </div>
                  </div>
                  <div className="control-message-content">
                    <p>2026 Automation tools for marketing...</p>
                    <span className="sent-success">✓ Posted successfully</span>
                  </div>
                </div>

                {/* Reply Card - Awaiting Approval */}
                <div className="control-message-card pending">
                  <div className="card-status-icon pending">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="2"/>
                      <path d="M12 6v6l4 2" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </div>
                  <div className="control-card-header">
                    <span className="message-type-badge post">REPLY</span>
                    <div className="control-user-avatar">
                      <svg width="24" height="24" viewBox="0 0 40 40" fill="none">
                        <circle cx="20" cy="20" r="20" fill="#2563EB"/>
                        <ellipse cx="20" cy="26" rx="10" ry="8" fill="white"/>
                        <circle cx="20" cy="16" r="8" fill="white"/>
                        <circle cx="16" cy="15" r="2" fill="#2563EB"/>
                        <circle cx="24" cy="15" r="2" fill="#2563EB"/>
                        <line x1="20" y1="8" x2="20" y2="4" stroke="white" strokeWidth="2"/>
                        <circle cx="20" cy="3" r="2" fill="#2563EB"/>
                        <circle cx="12" cy="12" r="2.5" fill="white"/>
                        <circle cx="28" cy="12" r="2.5" fill="white"/>
                      </svg>
                    </div>
                    <div className="control-user-info">
                      <span className="control-username">splitgalaxy</span>
                      <span className="control-status pending">Awaiting Approval</span>
                    </div>
                  </div>
                  <div className="control-message-content">
                    <p>Here are some things that helped me scale...</p>
                    <div className="approval-buttons">
                      <button className="btn-approve-new">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                          <path d="M20 6L9 17L4 12" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Approve
                      </button>
                      <button className="btn-dismiss-new">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                          <path d="M18 6L6 18M6 6l12 12" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Dismiss
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating Control Badge */}
              <div className="floating-control-badge">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                You Control Every Message
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = () => {
  return (
    <section className="cta-section">
      <div className="cta-grid"></div>
      <div className="cta-grain"></div>
      <div className="container">
        <div className="cta-card">
          <h2 className="cta-title">
            <span className="text-gradient">BluePigeon</span> automates your Reddit marketing, and you stay in <span className="text-green">full control</span>
          </h2>
          <button className="btn-cta-primary" onClick={() => window.open('https://platform.bluepigeon.app', '_blank')}>
            Get started for free
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M3 8H13M13 8L9 4M13 8L9 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      </div>
    </section>
  );
};

// Pricing Section
const Pricing = () => {
  const [isYearly, setIsYearly] = useState(false);

  const plans = [
    {
      name: 'Free',
      desc: 'Perfect for trying out Wedge',
      monthlyPrice: 0,
      buttonText: 'Try Free',
      buttonLink: 'https://platform.bluepigeon.app',
      features: [
        '1 project',
        '3 subreddits',
        '5 keywords',
        '3 scans/month',
        '10 AI replies/month',
        '5 posts/month'
      ]
    },
    {
      name: 'Starter',
      desc: 'For individuals getting started',
      monthlyPrice: 45,
      buttonText: 'Subscribe',
      buttonLink: 'https://platform.bluepigeon.app/subscription',
      features: [
        '1 project',
        '5 subreddits',
        '10 keywords',
        '15 scans/mo',
        '150 replies/mo',
        '20 posts/mo',
        'Priority email support'
      ]
    },
    {
      name: 'Growth',
      desc: 'For individuals looking to level up',
      monthlyPrice: 99,
      popular: true,
      buttonText: 'Subscribe',
      buttonLink: 'https://platform.bluepigeon.app/subscription',
      features: [
        '1 project',
        '10 subreddits',
        '10 keywords',
        '30 scans/mo',
        '400 replies/mo',
        '60 posts/mo',
        'Priority email support',
        'Setup fast-track support'
      ]
    },
    {
      name: 'Max',
      desc: 'For agencies and teams',
      monthlyPrice: 299,
      buttonText: 'Subscribe',
      buttonLink: 'https://platform.bluepigeon.app/subscription',
      features: [
        '3 projects',
        '10 subreddits/project',
        '15 keywords/project',
        '100 scans/mo',
        '1200 replies/mo',
        '200 posts/mo',
        '24/7 support',
        'Setup fast-track support'
      ]
    }
  ];

  const getPrice = (monthlyPrice) => {
    if (monthlyPrice === 0) return 0;
    if (isYearly) {
      return Math.round(monthlyPrice * 0.85); // 15% discount
    }
    return monthlyPrice;
  };

  return (
    <section id="pricing" className="pricing">
      <div className="pricing-bg-grid"></div>
      <div className="pricing-bg-grain"></div>
      <div className="container">
        <span className="section-label">PRICING</span>
        <h2 className="section-title">
          Plans for <span className="text-cursive">every</span> Stage
        </h2>
        <p className="section-subtitle">
          Whether you're just starting out or scaling up, we've got you covered.
        </p>

        <div className="billing-toggle">
          <span className={!isYearly ? 'active' : ''}>Monthly</span>
          <button
            className={`toggle-btn ${isYearly ? 'yearly' : ''}`}
            onClick={() => setIsYearly(!isYearly)}
          >
            <span className="toggle-knob"></span>
          </button>
          <span className={isYearly ? 'active' : ''}>Yearly</span>
          <span className="save-tag">Save 15%</span>
        </div>

        <div className="pricing-grid">
          {plans.map((plan, idx) => (
            <div key={idx} className={`pricing-card ${plan.popular ? 'popular' : ''}`}>
              {plan.popular && <div className="card-badge popular">Most Popular</div>}

              <div className="plan-header">
                <h3 className="plan-name">{plan.name}</h3>
              </div>

              <p className="plan-desc">{plan.desc}</p>

              <div className="plan-price">
                <span className="currency">$</span>
                <span className="amount">{getPrice(plan.monthlyPrice)}</span>
                <span className="period">/{isYearly ? 'month' : 'month'}</span>
              </div>
              {isYearly && plan.monthlyPrice > 0 && (
                <span className="billing-note">Billed ${getPrice(plan.monthlyPrice) * 12}/year</span>
              )}

              <ul className="plan-features">
                {plan.features.map((feature, i) => (
                  <li key={i}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <circle cx="8" cy="8" r="8" fill={plan.popular ? '#2563EB' : '#E5E7EB'}/>
                      <path d="M5 8L7 10L11 6" stroke={plan.popular ? 'white' : '#6B7280'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    {feature}
                  </li>
                ))}
              </ul>

              <button className={`plan-btn ${plan.popular ? 'primary' : ''}`} onClick={() => window.open(plan.buttonLink, '_blank')}>
                {plan.buttonText}
              </button>
            </div>
          ))}
        </div>

        <div className="pricing-watermark">BluePigeon</div>
      </div>
    </section>
  );
};

// FAQ Section
const FAQ = () => {
  const [openIdx, setOpenIdx] = useState(null);

  const faqs = [
    { q: 'How does BluePigeon find customers on Reddit?', a: 'BluePigeon uses advanced AI to scan Reddit 24/7, identifying conversations where people are actively looking for solutions like yours. We analyze post content, comments, and user intent to surface the most relevant leads.' },
    { q: 'How does BluePigeon Post and Reply in Reddit?', a: 'You can connect your Reddit account in BluePigeon, when we find relevant opportunities, we draft personalized responses that you can review, edit, and approve before sending.' },
    { q: 'Can BluePigeon get my Reddit account banned?', a: 'We\'ve designed BluePigeon with Reddit\'s guidelines in mind. All messages go through your account with your approval, respecting rate limits and community rules.' },
    { q: 'Why use BluePigeon instead of bot accounts?', a: 'Bot accounts violate Reddit\'s terms of service and get banned quickly. BluePigeon works through your real account, building genuine relationships and reputation.' },
    { q: 'Can I add additional projects to my account?', a: 'Yes! You can manage multiple products or projects from a single BluePigeon account. Each project can have its own keywords, target subreddits, and messaging templates.' }
  ];

  return (
    <section className="faq">
      <div className="container">
        <h2 className="section-title">
          Frequently Asked <span className="text-gradient">Questions</span>
        </h2>
        <p className="section-subtitle">
          Can't find what you're looking for? Feel free to <a href="mailto:support@bluepigeon.app" className="link-underline">reach out</a>.
        </p>

        <div className="faq-list">
          {faqs.map((faq, idx) => (
            <div
              key={idx}
              className={`faq-item ${openIdx === idx ? 'open' : ''}`}
              onClick={() => setOpenIdx(openIdx === idx ? null : idx)}
            >
              <div className="faq-question">
                <h3>{faq.q}</h3>
                <svg className="faq-chevron" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="faq-answer">
                <p>{faq.a}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  const handleFooterNavClick = (e, sectionId) => {
    e.preventDefault();
    const isHomePage = window.location.pathname === '/';

    if (isHomePage) {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else {
      window.location.href = '/#' + sectionId;
    }
  };

  return (
    <footer className="footer">
      {/* CTA Banner */}
      <div className="footer-cta">
        <h2 className="footer-cta-title">
          Acquisition made <span className="text-cursive">easy</span>
        </h2>
        <div className="footer-cta-buttons">
          <button className="btn-cta-primary" onClick={() => window.open('https://platform.bluepigeon.app', '_blank')}>
            Find new Customers
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14"/><path d="M12 5l7 7-7 7"/>
            </svg>
          </button>
          <button className="btn-demo" onClick={() => window.location.href = 'mailto:support@bluepigeon.app?subject=Schedule a Demo'}>
            Schedule a Demo
          </button>
        </div>
      </div>

      <div className="footer-divider"></div>

      <div className="container">
        <div className="footer-grid">
          <div className="footer-brand">
            <Link to="/" className="logo">
              <div className="logo-icon">
                <svg width="28" height="28" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                  <path fill="#0118A1" d="M10.306 12.482c-.15-.11-.27-.1-.791.15c-.52.25-1.241.67-2.693 1.431a.35.35 0 0 0-.16.48a.34.34 0 0 0 .47.16c1.002-.46 1.772-.81 2.253-1c-.18.59-.48 1.441-.821 2.272q-.405 1.031-.94 2.002a1.8 1.8 0 0 1-.411.56h-.13l-.38-.06c-.361-.09-.721-.22-.922-.28l-4.153-1.301l1.33-.571a.32.32 0 0 0 .191-.41a.31.31 0 0 0-.4-.18c-.64.22-2.522.92-2.543.93a.32.32 0 0 0-.19.4q.28.862.44 1.752c.18.911.311 1.822.461 2.733q.054.661.18 1.31c.06.3.223.57.46.762c.237.147.513.217.791.2q.652-.08 1.272-.3c.68-.2 1.36-.42 2.001-.651c.64-.23 1.332-.48 2.002-.73a75 75 0 0 0 2.502-1.002c.5-.22 1.001-.46 1.632-.76a4.3 4.3 0 0 0 1.321-.921c.219-.248.334-.57.32-.901a12 12 0 0 0-1.23-3.193a11.7 11.7 0 0 0-1.863-2.882m1.721 6.465a4 4 0 0 1-.76.41L7.152 21.01l-3.913 1.45c-.2.07-.48.2-.75.27s-.43.281-.53-.45c-.081-.36-.121-.74-.151-.91c-.22-.901-.4-1.812-.64-2.713q-.136-.57-.33-1.12c.57.23 1.57.64 2.571 1c.771.29 1.522.57 2.062.75q.707.272 1.451.401c.343.07.698.013 1.001-.16a6.2 6.2 0 0 0 1.462-2.522q.612-1.592 1-3.253c.3.51.661 1.25 1.002 2.002a11 11 0 0 1 1 2.872c-.04.13-.21.22-.36.32"/>
                  <path fill="#0118A1" d="M15.49 1.892c.22.36.295.789.21 1.201c0 .12-.44.69-.54.951a.62.62 0 0 0 0 .53c.083.152.203.28.35.37c.18.121.42.181.57.281c.298.2.55.458.741.76a.82.82 0 0 1 .13.802q-.096.182-.25.32a2 2 0 0 1-.37.24a2.4 2.4 0 0 0-.43.19a.45.45 0 0 0-.22.3a.75.75 0 0 0 .19.561c.21.26.64.59.72.8a.88.88 0 0 1-.11.861c-.403.43-.89.77-1.431 1.001a.31.31 0 0 0-.14.42a.32.32 0 0 0 .43.14a5 5 0 0 0 1.691-1.1a1.52 1.52 0 0 0 .25-1.522a3.3 3.3 0 0 0-.49-1q.237-.114.44-.28c.19-.147.346-.331.461-.541a1.5 1.5 0 0 0 0-1.371a3.4 3.4 0 0 0-1.13-1.392l-.291-.16c.162-.268.287-.558.37-.86a2.93 2.93 0 0 0-.4-2.403a1.84 1.84 0 0 0-1.632-.8a2.19 2.19 0 0 0-1.861 1.16c-.333.85-.558 1.738-.67 2.643c-.19.866-.483 1.706-.872 2.502a6.9 6.9 0 0 1-1.511 2.062c-.18.16-.59.56-1.001.92a5 5 0 0 1-.61.451a8.4 8.4 0 0 0-1.442-1.2c.13-.944.13-1.9 0-2.843a4.8 4.8 0 0 0-.81-2.002c-.31-.42-1.001-.95-1.402-1.541a.87.87 0 0 1-.24-.761a.31.31 0 0 1 .24-.18c.21-.068.43-.098.65-.09c.48-.003.956.1 1.392.3c.13.08.15.23.2.34q.082.213.21.4q.076.106.18.18a.8.8 0 0 0 .411.16q.399.005.79-.06c.213.003.423.044.621.12c.192.082.366.201.51.351q.122.148.18.33q.067.19.06.39c0 .07-.11.341-.12.511a.53.53 0 0 0 .1.41a.35.35 0 0 0 .501 0s.18-.08.28-.9a1.9 1.9 0 0 0-.06-.67a1.7 1.7 0 0 0-.37-.541a2.15 2.15 0 0 0-.75-.64a3.75 3.75 0 0 0-1.592-.321a2.4 2.4 0 0 0-.19-.48a1.15 1.15 0 0 0-.4-.45A4.4 4.4 0 0 0 4.66.18a1.72 1.72 0 0 0-1.7 1.09a1.77 1.77 0 0 0 .21 1.482a15.6 15.6 0 0 0 1.792 1.832c.37.44.64.955.79 1.51c.177.775.248 1.57.21 2.363a2 2 0 0 0-.31-.07a3.6 3.6 0 0 0-1.691.21A2.69 2.69 0 0 0 2.118 11a3.1 3.1 0 0 0 1.251 2.443a2 2 0 0 0 0 .22q.02.324.1.64l.14.57c.063.331.24.63.501.842c.22.11.53.15.86-.28l.21-.321l.782-1.111a.36.36 0 0 0-.56-.44l-.842 1.19l-.08.12a.8.8 0 0 1-.08-.16l-.12-.55c0-.14 0-.29-.06-.44a1.1 1.1 0 0 1 0-.36a.42.42 0 0 0-.15-.36a2.33 2.33 0 0 1-1.001-1.883A1.71 1.71 0 0 1 4.27 9.59a2.55 2.55 0 0 1 1.231-.13c.388.085.739.292 1.001.59c.14.15.49.621.76.861a1 1 0 0 0 .551.29a2 2 0 0 0 1.151-.46a19 19 0 0 0 1.562-1.291a8 8 0 0 0 1.751-2.452c.446-.877.782-1.804 1.001-2.763c.08-.803.258-1.593.53-2.352a1.09 1.09 0 0 1 .931-.59c.32 0 .581.28.751.6m8.488 13.301a12.5 12.5 0 0 0-.791-3.003a2.4 2.4 0 0 0-.51-.84a1.4 1.4 0 0 0-.781-.32c-.484.002-.965.07-1.432.2q-1.04.293-2.111.44a2.56 2.56 0 0 1-1.272-.22a.36.36 0 0 0-.3.65a3.4 3.4 0 0 0 1.592.37q1.137-.081 2.252-.32q.431-.092.87-.13c.581 0 .54.21.611.39q.326.935.53 1.902c.49 2.002 0 2.162-.88 2.092c-1.151-.09-2.372-.74-3.443-.61a1.06 1.06 0 0 0-.56.24q-.273.314-.501.66q-.36.397-.771.741q-.399.346-.86.6l-1.112.491a.31.31 0 0 0-.19.4a.31.31 0 0 0 .39.2l1.221-.45a6 6 0 0 0 1.001-.61a7.5 7.5 0 0 0 .911-.761q.206-.218.38-.46a.24.24 0 0 1 .13-.09c.511.02 1.014.135 1.482.34q.86.338 1.771.49a2.87 2.87 0 0 0 1.792-.37a1.18 1.18 0 0 0 .51-.76a3.9 3.9 0 0 0 .07-1.262"/>
                </svg>
              </div>
              <span className="logo-text">Blue Pigeon</span>
            </Link>
            <p>Get customers from Reddit on auto-pilot.</p>
            <div className="footer-socials">
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
              </a>
              <a href="https://youtube.com" target="_blank" rel="noopener noreferrer" aria-label="YouTube">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
              </a>
              <a href="https://x.com" target="_blank" rel="noopener noreferrer" aria-label="X">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z"/></svg>
              </a>
            </div>
          </div>

          <div className="footer-links">
            <div className="link-column">
              <h4>Contact</h4>
              <a href="mailto:support@bluepigeon.app" className="email-link">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="4" width="20" height="16" rx="2"/>
                  <path d="M22 7l-10 7L2 7"/>
                </svg>
                support@bluepigeon.app
              </a>
            </div>
            <div className="link-column">
              <h4>Product</h4>
              <a href="#features" onClick={(e) => handleFooterNavClick(e, 'features')}>Features</a>
              <a href="#pricing" onClick={(e) => handleFooterNavClick(e, 'pricing')}>Pricing</a>
              <a href="#how-it-works" onClick={(e) => handleFooterNavClick(e, 'how-it-works')}>How It Works</a>
            </div>
            <div className="link-column">
              <h4>Legal</h4>
              <Link to="/privacy-policy">Privacy Policy</Link>
              <Link to="/terms-of-service">Terms of Service</Link>
            </div>
          </div>
        </div>

        <div className="footer-bottom">
          <p>&copy; 2026 BluePigeon. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

// Privacy Policy Page
const PrivacyPolicy = () => {
  const [activeSection, setActiveSection] = useState('introduction');

  const sections = [
    { id: 'introduction', title: 'Introduction' },
    { id: 'interpretation', title: 'Interpretation and Definitions' },
    { id: 'collecting', title: 'Collecting and Using Your Personal Data' },
    { id: 'gdpr', title: 'GDPR Privacy' },
    { id: 'ccpa', title: 'CCPA Privacy Notice' },
    { id: 'children', title: "Children's Privacy" },
    { id: 'links', title: 'Links to Other Websites' },
    { id: 'changes', title: 'Changes to this Privacy Policy' },
    { id: 'contact', title: 'Contact Us' }
  ];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Scroll spy - detect which section is currently in view
  useEffect(() => {
    const handleScroll = () => {
      const sectionElements = sections.map(section => ({
        id: section.id,
        element: document.getElementById(section.id)
      })).filter(s => s.element);

      // Find the section that's currently in view
      let currentSection = sections[0].id;

      for (const { id, element } of sectionElements) {
        const rect = element.getBoundingClientRect();
        // Check if section top is above the middle of viewport
        if (rect.top <= 200) {
          currentSection = id;
        }
      }

      setActiveSection(currentSection);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Run once on mount

    return () => window.removeEventListener('scroll', handleScroll);
  }, [sections]);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="privacy-page">
      <Header />

      <div className="privacy-container">
        <main className="privacy-content">
          <h1 className="privacy-title">Privacy Policy</h1>
          <div className="privacy-meta">
            <span>BluePigeon Inc.</span>
            <span>Last Updated: January 2025</span>
          </div>

          <section id="introduction" className="privacy-section">
            <h2>Introduction</h2>
            <p>This Privacy Policy describes our policies and procedures on the collection, use, and disclosure of your information when you use the BluePigeon platform (the "Service"). It also explains your privacy rights and how the law protects you.</p>
            <p>We use your Personal Data to provide and improve the Service. By using the Service, you agree to the collection and use of information in accordance with this Privacy Policy.</p>
            <p>By accessing or using the Service, you acknowledge that you have read, understood, and agree to be bound by this Privacy Policy. If you do not agree with this Privacy Policy, please do not use the Service.</p>
            <p><strong>Eligibility:</strong> The Service is intended for users who are at least 18 years of age. By using the Service, you represent and warrant that you are at least 18 years old.</p>
          </section>

          <section id="interpretation" className="privacy-section">
            <h2>Interpretation and Definitions</h2>
            <h3>Interpretation</h3>
            <p>The words of which the initial letter is capitalized have meanings defined under the following conditions. The following definitions shall have the same meaning regardless of whether they appear in singular or in plural.</p>
            <h3>Definitions</h3>
            <p>For the purposes of this Privacy Policy:</p>
            <ul>
              <li><strong>Account</strong> means a unique account created for you to access our Service or parts of our Service.</li>
              <li><strong>Company</strong> (referred to as either "the Company", "We", "Us" or "Our" in this Agreement) refers to BluePigeon Inc.</li>
              <li><strong>Cookies</strong> are small files that are placed on your computer, mobile device or any other device by a website, containing the details of your browsing history on that website among its many uses.</li>
              <li><strong>Device</strong> means any device that can access the Service such as a computer, a cellphone or a digital tablet.</li>
              <li><strong>Personal Data</strong> is any information that relates to an identified or identifiable individual.</li>
              <li><strong>Service</strong> refers to the BluePigeon website and platform.</li>
              <li><strong>Usage Data</strong> refers to data collected automatically, either generated by the use of the Service or from the Service infrastructure itself.</li>
            </ul>
          </section>

          <section id="collecting" className="privacy-section">
            <h2>Collecting and Using Your Personal Data</h2>
            <h3>Types of Data Collected</h3>
            <h4>Personal Data</h4>
            <p>While using our Service, we may ask you to provide us with certain personally identifiable information that can be used to contact or identify you. Personally identifiable information may include, but is not limited to:</p>
            <ul>
              <li>Email address</li>
              <li>First name and last name</li>
              <li>Usage Data</li>
              <li>Reddit account information (with your permission)</li>
            </ul>
            <h4>Usage Data</h4>
            <p>Usage Data is collected automatically when using the Service. This may include information such as your Device's Internet Protocol address (IP address), browser type, browser version, the pages of our Service that you visit, the time and date of your visit, the time spent on those pages, and other diagnostic data.</p>
            <h3>Use of Your Personal Data</h3>
            <p>The Company may use Personal Data for the following purposes:</p>
            <ul>
              <li><strong>To provide and maintain our Service</strong>, including to monitor the usage of our Service.</li>
              <li><strong>To manage your Account:</strong> to manage your registration as a user of the Service.</li>
              <li><strong>For the performance of a contract:</strong> the development, compliance and undertaking of the purchase contract for the products, items or services you have purchased.</li>
              <li><strong>To contact you:</strong> To contact you by email, telephone calls, SMS, or other equivalent forms of electronic communication.</li>
              <li><strong>To provide you</strong> with news, special offers and general information about other goods, services and events which we offer.</li>
            </ul>
          </section>

          <section id="gdpr" className="privacy-section">
            <h2>GDPR Privacy</h2>
            <h3>Legal Basis for Processing Personal Data under GDPR</h3>
            <p>We may process Personal Data under the following conditions:</p>
            <ul>
              <li><strong>Consent:</strong> You have given your consent for processing Personal Data for one or more specific purposes.</li>
              <li><strong>Performance of a contract:</strong> Provision of Personal Data is necessary for the performance of an agreement with you and/or for any pre-contractual obligations thereof.</li>
              <li><strong>Legal obligations:</strong> Processing Personal Data is necessary for compliance with a legal obligation to which the Company is subject.</li>
              <li><strong>Vital interests:</strong> Processing Personal Data is necessary in order to protect your vital interests or of another natural person.</li>
              <li><strong>Legitimate interests:</strong> Processing Personal Data is necessary for the purposes of the legitimate interests pursued by the Company.</li>
            </ul>
            <h3>Your Rights under the GDPR</h3>
            <p>If you are a resident of the European Economic Area (EEA), you have certain data protection rights. BluePigeon aims to take reasonable steps to allow you to correct, amend, delete, or limit the use of your Personal Data.</p>
          </section>

          <section id="ccpa" className="privacy-section">
            <h2>CCPA Privacy Notice</h2>
            <p>This privacy notice section for California residents supplements the information contained in our Privacy Policy and it applies solely to all visitors, users, and others who reside in the State of California.</p>
            <h3>Categories of Personal Information Collected</h3>
            <p>We collect information that identifies, relates to, describes, references, is capable of being associated with, or could reasonably be linked, directly or indirectly, with a particular consumer or device.</p>
            <h3>Your Rights under the CCPA</h3>
            <p>The CCPA provides California residents with specific rights regarding their personal information. This section describes your CCPA rights and explains how to exercise those rights.</p>
            <ul>
              <li><strong>Right to Know:</strong> You have the right to request that we disclose certain information to you about our collection and use of your personal information.</li>
              <li><strong>Right to Delete:</strong> You have the right to request that we delete any of your personal information that we collected from you.</li>
              <li><strong>Right to Non-Discrimination:</strong> We will not discriminate against you for exercising any of your CCPA rights.</li>
            </ul>
          </section>

          <section id="children" className="privacy-section">
            <h2>Children's Privacy</h2>
            <p>Our Service does not address anyone under the age of 18. We do not knowingly collect personally identifiable information from anyone under the age of 18. If you are a parent or guardian and you are aware that your child has provided us with Personal Data, please contact us.</p>
            <p>If we become aware that we have collected Personal Data from anyone under the age of 18 without verification of parental consent, we take steps to remove that information from our servers.</p>
          </section>

          <section id="links" className="privacy-section">
            <h2>Links to Other Websites</h2>
            <p>Our Service may contain links to other websites that are not operated by us. If you click on a third-party link, you will be directed to that third party's site. We strongly advise you to review the Privacy Policy of every site you visit.</p>
            <p>We have no control over and assume no responsibility for the content, privacy policies or practices of any third-party sites or services.</p>
          </section>

          <section id="changes" className="privacy-section">
            <h2>Changes to this Privacy Policy</h2>
            <p>We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page.</p>
            <p>We will let you know via email and/or a prominent notice on our Service, prior to the change becoming effective and update the "Last updated" date at the top of this Privacy Policy.</p>
            <p>You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy are effective when they are posted on this page.</p>
          </section>

          <section id="contact" className="privacy-section">
            <h2>Contact Us</h2>
            <p>If you have any questions about this Privacy Policy, you can contact us:</p>
            <ul>
              <li>By email: privacy@bluepigeon.app</li>
              <li>By visiting the contact page on our website</li>
            </ul>
          </section>
        </main>

        <aside className="privacy-sidebar">
          <div className="sidebar-sticky">
            <h3>CONTENTS</h3>
            <nav className="sidebar-nav">
              {sections.map((section) => (
                <button
                  key={section.id}
                  className={`sidebar-link ${activeSection === section.id ? 'active' : ''}`}
                  onClick={() => scrollToSection(section.id)}
                >
                  {section.title}
                </button>
              ))}
            </nav>
          </div>
        </aside>
      </div>

      <Footer />
    </div>
  );
};

// Terms of Service Page
const TermsOfService = () => {
  const [activeSection, setActiveSection] = useState('interpretation');

  const sections = [
    { id: 'interpretation', title: 'Interpretation and Definitions' },
    { id: 'acknowledgment', title: 'Acknowledgment' },
    { id: 'user-accounts', title: 'User Accounts' },
    { id: 'subscriptions', title: 'Subscriptions and Payments' },
    { id: 'user-content', title: 'User Content and Conduct' },
    { id: 'intellectual-property', title: 'Intellectual Property' },
    { id: 'feedback', title: 'Feedback' },
    { id: 'third-party', title: 'Third-Party Services' },
    { id: 'termination', title: 'Termination' },
    { id: 'limitation', title: 'Limitation of Liability' },
    { id: 'disclaimer', title: '"AS IS" Disclaimer' },
    { id: 'indemnification', title: 'Indemnification' },
    { id: 'governing-law', title: 'Governing Law' },
    { id: 'changes', title: 'Changes to Terms' },
    { id: 'contact', title: 'Contact Us' }
  ];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const sectionElements = sections.map(section => ({
        id: section.id,
        element: document.getElementById(section.id)
      })).filter(s => s.element);

      let currentSection = sections[0].id;

      for (const { id, element } of sectionElements) {
        const rect = element.getBoundingClientRect();
        if (rect.top <= 200) {
          currentSection = id;
        }
      }

      setActiveSection(currentSection);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, [sections]);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="privacy-page">
      <Header />

      <div className="privacy-container">
        <main className="privacy-content">
          <h1 className="privacy-title">Terms of Service</h1>
          <div className="privacy-meta">
            <span>BluePigeon Inc.</span>
            <span>Last Updated: January 2025</span>
          </div>

          <section id="interpretation" className="privacy-section">
            <h2>Interpretation and Definitions</h2>
            <h3>Interpretation</h3>
            <p>The words of which the initial letter is capitalized have meanings defined under the following conditions. The following definitions shall have the same meaning regardless of whether they appear in singular or in plural.</p>
            <h3>Definitions</h3>
            <p>For the purposes of these Terms and Conditions:</p>
            <ul>
              <li><strong>Affiliate</strong> means an entity that controls, is controlled by, or is under common control with a party, where "control" means ownership of 50% or more of the shares, equity interest, or other securities entitled to vote for election of directors or other managing authority.</li>
              <li><strong>Account</strong> means a unique account created for You to access our Service or parts of our Service.</li>
              <li><strong>Company</strong> (referred to as either "the Company", "We", "Us" or "Our" in this Agreement) refers to BluePigeon Inc.</li>
              <li><strong>Country</strong> refers to the United States.</li>
              <li><strong>Device</strong> means any device that can access the Service such as a computer, a cellphone, or a digital tablet.</li>
              <li><strong>Feedback</strong> means any suggestions, comments, ideas, improvements, or other feedback You provide regarding the Service.</li>
              <li><strong>Platform</strong> refers to the BluePigeon Reddit marketing automation platform that allows users to find leads, create replies, and automate outreach on Reddit.</li>
              <li><strong>Service</strong> refers to the Platform and Website.</li>
              <li><strong>Subscription</strong> means the paid subscription plan that provides access to the Service's features and capabilities.</li>
              <li><strong>Terms and Conditions</strong> (also referred to as "Terms") mean these Terms and Conditions that form the entire agreement between You and the Company regarding the use of the Service.</li>
              <li><strong>Third-Party Services</strong> means any applications, platforms, or services that You connect to through our Platform as part of Your workflows and automations.</li>
              <li><strong>User Content</strong> means any data, information, replies, prompts, configurations, or other content that You input, upload, or create through the Service.</li>
              <li><strong>Website</strong> refers to BluePigeon, accessible from the BluePigeon website.</li>
              <li><strong>You</strong> means the individual accessing or using the Service, or the company, or other legal entity on behalf of which such individual is accessing or using the Service, as applicable.</li>
            </ul>
          </section>

          <section id="acknowledgment" className="privacy-section">
            <h2>Acknowledgment</h2>
            <p>These are the Terms and Conditions governing the use of this Service and the agreement that operates between You and the Company. These Terms and Conditions set out the rights and obligations of all users regarding the use of the Service.</p>
            <p>Your access to and use of the Service is conditioned on Your acceptance of and compliance with these Terms and Conditions. These Terms and Conditions apply to all visitors, users, and others who access or use the Service.</p>
            <p>By accessing or using the Service You agree to be bound by these Terms and Conditions. If You disagree with any part of these Terms and Conditions, then You may not access the Service.</p>
            <p>You represent that you are over the age of 18. The Company does not permit those under 18 to use the Service.</p>
            <p>Your access to and use of the Service is also conditioned on Your acceptance of and compliance with the Privacy Policy of the Company. Our Privacy Policy describes Our policies and procedures on the collection, use, and disclosure of Your personal information when You use the Application or the Website and tells You about Your privacy rights and how the law protects You. Please read Our Privacy Policy carefully before using Our Service.</p>
          </section>

          <section id="user-accounts" className="privacy-section">
            <h2>User Accounts</h2>
            <p>When You create an account with Us, You must provide Us information that is accurate, complete, and current at all times. Failure to do so constitutes a breach of the Terms, which may result in immediate termination of Your account on Our Service.</p>
            <p>You are responsible for safeguarding the password that You use to access the Service and for any activities or actions under Your password, whether Your password is with Our Service or a Third-Party Social Media Service.</p>
            <p>You agree not to disclose Your password to any third party. You must notify Us immediately upon becoming aware of any breach of security or unauthorized use of Your account.</p>
            <p>You may not use as a username the name of another person or entity or that is not lawfully available for use, a name or trademark that is subject to any rights of another person or entity other than You without appropriate authorization, or a name that is otherwise offensive, vulgar, or obscene.</p>
          </section>

          <section id="subscriptions" className="privacy-section">
            <h2>Subscriptions and Payments</h2>
            <h3>Subscription Terms</h3>
            <p>Some parts of the Service are available only with a paid Subscription. You will be billed in advance on a recurring and periodic basis (such as monthly or annually), depending on the type of Subscription plan You select.</p>
            <p>At the end of each period, Your Subscription will automatically renew under the same conditions unless You cancel it or the Company cancels it.</p>
            <h3>Billing</h3>
            <p>You shall provide the Company with accurate and complete billing information including full name, address, state, zip code, telephone number, and valid payment method information.</p>
            <p>Should automatic billing fail to occur for any reason, the Company will issue an electronic invoice indicating that You must proceed manually, within a certain deadline date, with the full payment corresponding to the billing period as indicated on the invoice.</p>
            <h3>Subscription Cancellation</h3>
            <p>You may cancel Your Subscription renewal either through Your account settings page or by contacting the Company. You will not receive a refund for the fees You already paid for Your current Subscription period and You will be able to access the Service until the end of Your current Subscription period.</p>
            <h3>Fee Changes</h3>
            <p>The Company, in its sole discretion and at any time, may modify the Subscription fees. Any Subscription fee change will become effective at the end of the then-current Subscription period.</p>
            <p>The Company will provide You with reasonable prior notice of any change in Subscription fees to give You an opportunity to terminate Your Subscription before such change becomes effective.</p>
            <h3>Refunds</h3>
            <p>Except when required by law, paid Subscription fees are non-refundable. Certain refund requests for Subscriptions may be considered by the Company on a case-by-case basis and granted at the sole discretion of the Company.</p>
          </section>

          <section id="user-content" className="privacy-section">
            <h2>User Content and Conduct</h2>
            <h3>Your User Content</h3>
            <p>Our Service allows You to create replies, automated messages, and marketing content. You are responsible for the User Content that You create, upload, or process through the Service, including its legality, reliability, and appropriateness.</p>
            <p>By creating User Content on the Service, You grant Us the right and license to use, modify, perform, display, reproduce, and distribute such User Content on and through the Service solely for the purpose of providing and improving the Service.</p>
            <h3>Content Restrictions</h3>
            <p>You agree not to use the Service to:</p>
            <ul>
              <li>Create content that violates any applicable law or regulation</li>
              <li>Process personal data without proper consent or legal basis</li>
              <li>Engage in fraudulent, deceptive, or harmful activities</li>
              <li>Harass, abuse, or harm another person or entity</li>
              <li>Impersonate any person or entity, or falsely state or otherwise misrepresent Your affiliation</li>
              <li>Interfere with or disrupt the Service or servers or networks connected to the Service</li>
              <li>Attempt to gain unauthorized access to any portion of the Service or any other systems or networks</li>
              <li>Use the Service for any purpose that is illegal or prohibited by these Terms</li>
              <li>Create spam, phishing content, or malicious communications</li>
              <li>Violate Reddit's terms of service or community guidelines</li>
              <li>Violate the terms of service of any Third-Party Services connected to the Platform</li>
            </ul>
            <h3>Reddit Account Usage</h3>
            <p>When You connect Your Reddit account to our Platform, You are solely responsible for ensuring You have proper authorization and that Your use complies with Reddit's terms of service. The Company is not responsible for any issues arising from Your use of Reddit through our Platform, including account suspensions or bans.</p>
          </section>

          <section id="intellectual-property" className="privacy-section">
            <h2>Intellectual Property</h2>
            <p>The Service and its original content (excluding User Content provided by You or other users), features, and functionality are and will remain the exclusive property of the Company and its licensors.</p>
            <p>The Service is protected by copyright, trademark, and other laws of both the Country and foreign countries.</p>
            <p>Our trademarks and trade dress may not be used in connection with any product or service without the prior written consent of the Company.</p>
            <h3>Your User Content</h3>
            <p>You retain all rights to Your User Content. By using the Service, You grant the Company a non-exclusive, worldwide, royalty-free license to use, reproduce, modify, and display Your User Content solely for the purpose of providing and improving the Service.</p>
            <h3>AI-Generated Content</h3>
            <p>Content generated by AI features You use through the Service may be subject to additional intellectual property considerations. You are responsible for ensuring that any AI-generated content does not infringe on third-party intellectual property rights.</p>
          </section>

          <section id="feedback" className="privacy-section">
            <h2>Feedback</h2>
            <p>If You provide the Company with any Feedback, You hereby assign to the Company all rights in such Feedback and agree that the Company shall have the right to use and fully exploit such Feedback and related information in any manner it deems appropriate.</p>
            <p>The Company will treat any Feedback You provide as non-confidential and non-proprietary. You agree that You will not submit to the Company any information or ideas that You consider to be confidential or proprietary.</p>
          </section>

          <section id="third-party" className="privacy-section">
            <h2>Third-Party Services and Integrations</h2>
            <p>Our Service may contain links to third-party web sites or services that are not owned or controlled by the Company.</p>
            <p>The Company has no control over, and assumes no responsibility for, the content, privacy policies, or practices of any third party web sites or services. You further acknowledge and agree that the Company shall not be responsible or liable, directly or indirectly, for any damage or loss caused or alleged to be caused by or in connection with the use of or reliance on any such content, goods, or services available on or through any such web sites or services.</p>
            <p>The Platform allows You to connect and integrate with Third-Party Services including Reddit. Your use of Third-Party Services is subject to the terms and conditions and privacy policies of those services.</p>
            <p>The Company does not endorse, warrant, or guarantee the functionality, reliability, or security of any Third-Party Services. We are not responsible for any data loss, security breaches, or other issues arising from Your use of Third-Party Services.</p>
            <p>You are solely responsible for maintaining proper authentication credentials and permissions for any Third-Party Services You connect to the Platform.</p>
          </section>

          <section id="termination" className="privacy-section">
            <h2>Termination</h2>
            <p>We may terminate or suspend Your Account immediately, without prior notice or liability, for any reason whatsoever, including without limitation if You breach these Terms and Conditions.</p>
            <p>Upon termination, Your right to use the Service will cease immediately. If You wish to terminate Your Account, You may simply discontinue using the Service or contact Us to request account deletion.</p>
            <p>All provisions of the Terms which by their nature should survive termination shall survive termination, including, without limitation, ownership provisions, warranty disclaimers, indemnity, and limitations of liability.</p>
          </section>

          <section id="limitation" className="privacy-section">
            <h2>Limitation of Liability</h2>
            <p>Notwithstanding any damages that You might incur, the entire liability of the Company and any of its suppliers under any provision of this Terms and Your exclusive remedy for all of the foregoing shall be limited to the amount actually paid by You through the Service during the twelve (12) months preceding the claim or 100 USD if You haven't purchased anything through the Service.</p>
            <p>To the maximum extent permitted by applicable law, in no event shall the Company or its suppliers be liable for any special, incidental, indirect, or consequential damages whatsoever (including, but not limited to, damages for loss of profits, loss of data or other information, for business interruption, for personal injury, loss of privacy arising out of or in any way related to the use of or inability to use the Service, third-party software and/or third-party hardware used with the Service, or otherwise in connection with any provision of this Terms), even if the Company or any supplier has been advised of the possibility of such damages and even if the remedy fails of its essential purpose.</p>
            <p>The Company is not liable for any damages, losses, or liabilities arising from:</p>
            <ul>
              <li>Automated replies or content You create that produce errors, inaccuracies, or unintended results</li>
              <li>Actions taken through automations You create through the Platform</li>
              <li>Reddit account suspensions or bans resulting from Your use of the Service</li>
              <li>Unauthorized access to Your account due to Your failure to maintain security</li>
              <li>Violations of Reddit's or other Third-Party Service terms caused by Your content or automations</li>
            </ul>
            <p>Some states do not allow the exclusion of implied warranties or limitation of liability for incidental or consequential damages, which means that some of the above limitations may not apply. In these states, each party's liability will be limited to the greatest extent permitted by law.</p>
          </section>

          <section id="disclaimer" className="privacy-section">
            <h2>"AS IS" and "AS AVAILABLE" Disclaimer</h2>
            <p>The Service is provided to You "AS IS" and "AS AVAILABLE" and with all faults and defects without warranty of any kind. To the maximum extent permitted under applicable law, the Company, on its own behalf and on behalf of its Affiliates and its and their respective licensors and service providers, expressly disclaims all warranties, whether express, implied, statutory, or otherwise, with respect to the Service, including all implied warranties of merchantability, fitness for a particular purpose, title, and non-infringement, and warranties that may arise out of course of dealing, course of performance, usage, or trade practice.</p>
            <p>Without limitation to the foregoing, the Company provides no warranty or undertaking, and makes no representation of any kind that the Service will meet Your requirements, achieve any intended results, be compatible or work with any other software, applications, systems, or services, operate without interruption, meet any performance or reliability standards, or be error-free, or that any errors or defects can or will be corrected.</p>
            <p>Without limiting the foregoing, neither the Company nor any of the company's provider makes any representation or warranty of any kind, express or implied:</p>
            <ul>
              <li>As to the operation or availability of the Service, or the information, content, and materials or products included thereon</li>
              <li>That the Service will be uninterrupted or error-free</li>
              <li>As to the accuracy, reliability, or currency of any information or content provided through the Service</li>
              <li>That the Service, its servers, the content, or e-mails sent from or on behalf of the Company are free of viruses, scripts, trojan horses, worms, malware, timebombs, or other harmful components</li>
              <li>That AI-generated replies or content created through the Platform will perform as expected or produce accurate results</li>
            </ul>
          </section>

          <section id="indemnification" className="privacy-section">
            <h2>Indemnification</h2>
            <p>You agree to defend, indemnify, and hold harmless the Company and its licensees, licensors, employees, contractors, agents, officers, and directors from and against any and all claims, damages, obligations, losses, liabilities, costs or debt, and expenses (including but not limited to attorney's fees) arising from:</p>
            <ul>
              <li>Your use of and access to the Service</li>
              <li>Your violation of any term of these Terms</li>
              <li>Your violation of any third-party right, including without limitation any copyright, property, or privacy right</li>
              <li>Any claim that Your User Content caused damage to a third party</li>
              <li>Content or automations You create that violate any laws or third-party rights</li>
              <li>Your misuse of Reddit or other Third-Party Services connected through the Platform</li>
            </ul>
            <p>This defense and indemnification obligation will survive these Terms and Your use of the Service.</p>
          </section>

          <section id="governing-law" className="privacy-section">
            <h2>Governing Law</h2>
            <p>The laws of the Country, excluding its conflicts of law rules, shall govern this Terms and Your use of the Service. Your use of the Application may also be subject to other local, state, national, or international laws.</p>
            <h3>Disputes Resolution</h3>
            <p>If You have any concern or dispute about the Service, You agree to first try to resolve the dispute informally by contacting the Company.</p>
            <h3>For European Union (EU) Users</h3>
            <p>If You are a European Union consumer, you will benefit from any mandatory provisions of the law of the country in which You are resident.</p>
            <h3>United States Legal Compliance</h3>
            <p>You represent and warrant that (i) You are not located in a country that is subject to the United States government embargo, or that has been designated by the United States government as a "terrorist supporting" country, and (ii) You are not listed on any United States government list of prohibited or restricted parties.</p>
            <h3>Severability and Waiver</h3>
            <p>If any provision of these Terms is held to be unenforceable or invalid, such provision will be changed and interpreted to accomplish the objectives of such provision to the greatest extent possible under applicable law and the remaining provisions will continue in full force and effect.</p>
            <p>Except as provided herein, the failure to exercise a right or to require performance of an obligation under these Terms shall not affect a party's ability to exercise such right or require such performance at any time thereafter nor shall the waiver of a breach constitute a waiver of any subsequent breach.</p>
          </section>

          <section id="changes" className="privacy-section">
            <h2>Changes to These Terms and Conditions</h2>
            <p>We reserve the right, at Our sole discretion, to modify or replace these Terms at any time. If a revision is material, We will make reasonable efforts to provide at least 30 days' notice prior to any new terms taking effect. What constitutes a material change will be determined at Our sole discretion.</p>
            <p>By continuing to access or use Our Service after those revisions become effective, You agree to be bound by the revised terms. If You do not agree to the new terms, in whole or in part, please stop using the website and the Service.</p>
          </section>

          <section id="contact" className="privacy-section">
            <h2>Contact Us</h2>
            <p>If you have any questions about these Terms and Conditions, You can contact us:</p>
            <ul>
              <li>By email: support@bluepigeon.app</li>
            </ul>
          </section>
        </main>

        <aside className="privacy-sidebar">
          <div className="sidebar-sticky">
            <h3>CONTENTS</h3>
            <nav className="sidebar-nav">
              {sections.map((section) => (
                <button
                  key={section.id}
                  className={`sidebar-link ${activeSection === section.id ? 'active' : ''}`}
                  onClick={() => scrollToSection(section.id)}
                >
                  {section.title}
                </button>
              ))}
            </nav>
          </div>
        </aside>
      </div>

      <Footer />
    </div>
  );
};

// Home Page Component
const HomePage = () => {
  return (
    <>
      <Header />
      <Hero />
      <HowItWorks />
      <RealOpportunities />
      <RedditInsights />
      <ToolsBento />
      <StepsSection />

      <Pricing />
      <FAQ />
      <Footer />
    </>
  );
};

function App() {
  return (
    <BrowserRouter>
      <div className="App">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
        </Routes>
      </div>
    </BrowserRouter>
  );
}

export default App;
